import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { getExercises, updateExercise, deleteExercise, updateWorkout, transferWorkout, bulkCancelWorkouts, getAllExercises, createCustomExercise, getCustomExercises, createExercise, getClients, validateWorkoutConflict, getTrainingPrograms, getClientWorkouts, getTrainerLocations, createTrainerLocation, getClientExerciseHistory, getClientExerciseWorkingWeights, updateClientExerciseWorkingWeight, getUser } from '../services/api';
import { buildApiUrl } from '../config/api';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, Plus, Edit, Trash2, Clock, User, Hash, Repeat2, Dumbbell, AlertCircle, Armchair, Footprints, User as UserIcon, Hand, Zap, MessageSquare, Terminal, Check, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { useI18n } from '../i18n/context';
import { useLocalizedExerciseOptions } from '../i18n/useLocalizedExerciseOptions';
import { getIntlLocaleTag, getDateFnsLocale } from '../lib/localeRuntime';
import { API_GROUP_WORKOUT_COMMENT, API_LOCATION_ONLINE_LABEL } from '../i18n/workoutConstants';
import ExerciseConfirmDialog from './ExerciseConfirmDialog';
import ClientProfileModal from './ClientProfileModal';
import PlanAssignmentModal from './PlanAssignmentModal';

const MUSCLE_GROUP_META = [
  { value: 'chest', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'back', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'legs', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'shoulders', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'arms', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'core', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'cardio', color: 'bg-pink-100 text-pink-800 border-pink-300' },
  { value: 'other', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];

/** Текст комментария прошлой тренировки для блока «Предыдущая тренировка» (пусто — не показывать) */
function getPreviousWorkoutCommentText(workout) {
  const c = workout?.comment;
  if (c == null || typeof c !== 'string') return '';
  const trimmed = c.trim();
  if (!trimmed || trimmed === API_GROUP_WORKOUT_COMMENT) return '';
  return trimmed;
}

/** Имя для UI «Клиент» — только поле name, без никнейма */
function displayClientName(name) {
  if (name == null) return '';
  const n = String(name).trim();
  return n;
}

const WorkoutDetailModal = ({
  show,
  onHide,
  workout,
  trainerId,
  onAddExercise,
  onAddNote,
  onWorkoutUpdate,
  onExerciseUpdate,
  onOpenWorkout,
}) => {
  const { t, locale } = useI18n();
  const dfLocale = getDateFnsLocale(locale);
  const dateLocaleTag = getIntlLocaleTag(locale);

  const muscleGroups = useMemo(
    () =>
      MUSCLE_GROUP_META.map((m) => ({
        ...m,
        label: t(`plan.muscle.${m.value}`),
      })),
    [t]
  );

  const exerciseList = useMemo(
    () => [
      t('workout.classic.bench'),
      t('workout.classic.squatBarbell'),
      t('workout.classic.deadlift'),
      t('workout.classic.ohp'),
      t('workout.classic.pullUps'),
      t('workout.classic.pushUps'),
      t('workout.classic.squats'),
      t('workout.classic.lunges'),
      t('workout.classic.latPulldown'),
      t('workout.classic.dbRow'),
      t('workout.classic.latRaise'),
      t('workout.classic.barbellCurl'),
      t('workout.classic.tricepPushdown'),
      t('workout.classic.calfRaise'),
      t('workout.classic.crunches'),
      t('workout.classic.plank'),
      t('workout.classic.burpee'),
      t('workout.classic.other'),
    ],
    [t]
  );

  const buildSuggestAddToDict = useCallback(
    (rawName) => t('workout.suggestAddToDict', { name: String(rawName || '').trim() }),
    [t]
  );

  const parseSuggestAddToDict = useCallback((s) => {
    const str = String(s || '');
    const ruM = str.match(/^\+\s*Добавить\s+"([^"]*)"\s+в словарь$/);
    if (ruM) return ruM[1];
    const enM = str.match(/^\+\s*Add\s+"([^"]*)"\s+to dictionary$/);
    return enM ? enM[1] : null;
  }, []);

  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddingExercise, setIsAddingExercise] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    date: '',
    time: '',
    endTime: ''
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showRescheduleConflictAlert, setShowRescheduleConflictAlert] = useState(false);
  const [rescheduleConflictingWorkouts, setRescheduleConflictingWorkouts] = useState([]);
  const [isValidatingRescheduleConflict, setIsValidatingRescheduleConflict] = useState(false);
  const [showRescheduleSameSlotAlert, setShowRescheduleSameSlotAlert] = useState(false);

  // Comment state
  const [comment, setComment] = useState('');
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);

  // Client profile modal state
  const [showClientProfile, setShowClientProfile] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [isSwitchingClientWorkout, setIsSwitchingClientWorkout] = useState(false);
  const [groupSlotWorkoutIdsByClientId, setGroupSlotWorkoutIdsByClientId] = useState({});


  // Form states
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    sets: []
  });

  // State for exercise name suggestions
  const [exerciseSuggestions, setExerciseSuggestions] = useState([]);
  const [showExerciseDropdown, setShowExerciseDropdown] = useState(false);
  const [allExercises, setAllExercises] = useState([]);
  const localizedAllExercises = useLocalizedExerciseOptions(allExercises);
  const [customExercises, setCustomExercises] = useState([]);

  const exerciseNameForDisplay = useMemo(() => {
    const n = (exerciseForm.name || '').trim();
    if (!n) return '';
    const hit = localizedAllExercises.find((e) => e.value === n);
    return hit?.label || n;
  }, [exerciseForm.name, localizedAllExercises]);

  // Exercise confirmation dialog state
  const [showExerciseConfirmDialog, setShowExerciseConfirmDialog] = useState(false);
  const [pendingExerciseName, setPendingExerciseName] = useState('');
  const [pendingExerciseAction, setPendingExerciseAction] = useState(null);
  const [pendingMuscleGroup, setPendingMuscleGroup] = useState('other');

  // Exercise deletion confirmation dialog state
  const [showExerciseDeleteDialog, setShowExerciseDeleteDialog] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);


  // Duplicate exercise alert state
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateExerciseName, setDuplicateExerciseName] = useState('');

  // Cancel workout confirmation dialog state
  const [showCancelWorkoutDialog, setShowCancelWorkoutDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showRestoreWorkoutDialog, setShowRestoreWorkoutDialog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [canCancelFutureInSeries, setCanCancelFutureInSeries] = useState(false);
const [showSkipWorkoutDialog, setShowSkipWorkoutDialog] = useState(false);
const [isSkipping, setIsSkipping] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Inline exercise form state
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState(null);
  const exerciseSelectorRef = useRef(null);
  const exerciseSearchInputRef = useRef(null);
  const inlineEditFormRef = useRef(null);
  const [exerciseHistoryLoading, setExerciseHistoryLoading] = useState(false);
  const [exerciseHistoryError, setExerciseHistoryError] = useState('');
  const [exerciseLastSession, setExerciseLastSession] = useState(null);
  const [exerciseMaxWeight, setExerciseMaxWeight] = useState(null);
  const [exerciseHasHistory, setExerciseHasHistory] = useState(false);
  const exerciseHistoryDebounceRef = useRef(null);

  // Working weight quick-edit state (per exercise card)
  const [workingWeightEditExerciseId, setWorkingWeightEditExerciseId] = useState(null);
  const [workingWeightInput, setWorkingWeightInput] = useState('');
  const [isSavingWorkingWeight, setIsSavingWorkingWeight] = useState(false);
  const [workingWeightsByName, setWorkingWeightsByName] = useState({});

  // Load effective working weights for exercises in this workout (client-specific)
  useEffect(() => {
    const isGroup = Boolean(workout?.isGroup) || (Array.isArray(workout?.allClients) && workout.allClients.length > 1);
    const clientId = workout?.client_id;
    if (!show || isGroup || !clientId) {
      setWorkingWeightsByName({});
      return;
    }

    const names = (Array.isArray(exercises) ? exercises : [])
      .map(e => (e?.name || '').trim())
      .filter(Boolean);
    const unique = Array.from(new Set(names));
    if (unique.length === 0) {
      setWorkingWeightsByName({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await getClientExerciseWorkingWeights(clientId, unique);
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        const map = {};
        for (const r of rows) {
          if (r && r.exercise_name) {
            map[r.exercise_name] = r; // { working_weight, manual_working_weight, max_history_weight }
          }
        }
        if (!cancelled) setWorkingWeightsByName(map);
      } catch (e) {
        console.error('[WorkoutDetailModal] Failed to load working weights:', e);
        if (!cancelled) setWorkingWeightsByName({});
      }
    })();

    return () => { cancelled = true; };
  }, [show, workout?.id, workout?.client_id, workout?.isGroup, workout?.allClients, exercises]);

  // Scroll inline edit form into view when editing is opened (mobile-friendly)
  useEffect(() => {
    if (showAddExercise && editingExercise && inlineEditFormRef.current) {
      const el = inlineEditFormRef.current;
      // Defer to next paint so layout is stable
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [showAddExercise, editingExercise?.id]);

  // Fetch last session + max weight for selected exercise (client-specific)
  useEffect(() => {
    if (!showAddExercise) return;

    // Only for individual workouts
    const isGroup = Boolean(workout?.isGroup) || (Array.isArray(workout?.allClients) && workout.allClients.length > 1);
    const clientId = workout?.client_id;
    const exerciseName = (exerciseForm?.name || '').trim();

    // Reset when we can't/shouldn't fetch
    if (!clientId || isGroup || !exerciseName) {
      setExerciseHistoryLoading(false);
      setExerciseHistoryError('');
      setExerciseLastSession(null);
      setExerciseMaxWeight(null);
      return;
    }

    // Debounce to avoid spamming API while typing
    if (exerciseHistoryDebounceRef.current) {
      clearTimeout(exerciseHistoryDebounceRef.current);
    }

    exerciseHistoryDebounceRef.current = setTimeout(async () => {
      setExerciseHistoryLoading(true);
      setExerciseHistoryError('');

      try {
        const resp = await getClientExerciseHistory(clientId, exerciseName);
        const history = Array.isArray(resp?.data) ? resp.data : [];

        // Exclude current workout from "last session"
        const filtered = history.filter(h => h?.workout_id && h.workout_id !== workout?.id);
        const last = filtered[0] || null;

        // Compute max weight across history (excluding current workout)
        let maxW = null;
        for (const h of filtered) {
          const sets = Array.isArray(h?.sets) ? h.sets : [];
          for (const s of sets) {
            const w = s?.weight;
            if (typeof w === 'number' && isFinite(w)) {
              maxW = maxW === null ? w : Math.max(maxW, w);
            }
          }
        }

        setExerciseLastSession(last);
        setExerciseMaxWeight(maxW);
        setExerciseHasHistory(filtered.length > 0);
      } catch (e) {
        console.error('[WorkoutDetailModal] Error loading exercise history:', e);
        setExerciseLastSession(null);
        setExerciseMaxWeight(null);
        setExerciseHasHistory(false);
        setExerciseHistoryError(t('workout.err.historyLoad'));
      } finally {
        setExerciseHistoryLoading(false);
      }
    }, 350);

    return () => {
      if (exerciseHistoryDebounceRef.current) {
        clearTimeout(exerciseHistoryDebounceRef.current);
      }
    };
  }, [showAddExercise, workout?.id, workout?.client_id, workout?.isGroup, workout?.allClients, exerciseForm?.name]);

  // Training plans state
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanSelectorInForm, setShowPlanSelectorInForm] = useState(false);
  const [showPlanAssignment, setShowPlanAssignment] = useState(false);
  const [workoutPlanId, setWorkoutPlanId] = useState(null);

  // Previous workout state
  const [previousWorkout, setPreviousWorkout] = useState(null);
  const [previousWorkoutExercises, setPreviousWorkoutExercises] = useState([]);
  const [loadingPreviousWorkout, setLoadingPreviousWorkout] = useState(false);
  const [showPreviousWorkout, setShowPreviousWorkout] = useState(false);
  const [completedWorkouts, setCompletedWorkouts] = useState([]); // All completed workouts for navigation
  const [currentCompletedWorkoutIndex, setCurrentCompletedWorkoutIndex] = useState(0); // Current index in completed workouts array

  // Location state
  const [trainerLocations, setTrainerLocations] = useState([]);
  const [resolvedTrainerId, setResolvedTrainerId] = useState(null);
  const hasExercises = Array.isArray(exercises) && exercises.length > 0;
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [locationValue, setLocationValue] = useState('');
  const [locationType, setLocationType] = useState(null);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  // Removed set deletion confirmation dialog state as we now delete directly

  // Function to format date and time in Russian
  const formatWorkoutDateTime = (date, time, endTime) => {
    if (!date || !time) return '';
    
    try {
      // Create date in local timezone
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = time.split(':').map(Number);
      const workoutDate = new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
      
      // Format: "14:18, 18 сентября, четверг" or "14:18-15:18, 18 сентября, четверг"
      const timeFormat = endTime ? `HH:mm-${endTime}` : 'HH:mm';
      return format(workoutDate, `${timeFormat}, d MMMM, EEEE`, { locale: dfLocale });
    } catch (error) {
      console.error('Error formatting date:', error);
      return endTime ? `${time}-${endTime}, ${date}` : `${time}, ${date}`;
    }
  };

  // Function to check if workout is completed by time
  const isWorkoutCompletedByTime = (workout) => {
    if (!workout || !workout.date || !workout.time) return false;
    
    // If status is explicitly cancelled or transferred, it's not completed
    if (workout.status === 'cancelled' || workout.status === 'transferred') {
      return false;
    }
    
    // If status is explicitly completed, return true
    if (workout.status === 'completed') {
      return true;
    }
    
    try {
      const now = new Date();
      
      // Parse workout date and time
      const [year, month, day] = workout.date.split('-').map(Number);
      const [hours, minutes] = workout.time.split(':').map(Number);
      const workoutStartTime = new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
      
      // Calculate workout end time (handle overnight: end_time < start_time means next day)
      let workoutEndTime;
      if (workout.end_time) {
        const [endHours, endMinutes] = workout.end_time.split(':').map(Number);
        workoutEndTime = new Date(year, month - 1, day, endHours, endMinutes || 0, 0, 0);
        if (workoutEndTime <= workoutStartTime) {
          workoutEndTime.setDate(workoutEndTime.getDate() + 1);
        }
      } else {
        workoutEndTime = new Date(workoutStartTime.getTime() + 60 * 60 * 1000);
      }
      
      return now > workoutEndTime;
    } catch (error) {
      console.error('Error checking workout completion:', error);
      return false;
    }
  };


  // Load trainer locations
  const loadTrainerLocations = async () => {
    try {
      let trainerId = null;
      if (workout?.trainer_id) {
        trainerId = workout.trainer_id;
      } else {
        try {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            trainerId = user.id;
          }
        } catch (e) {
          console.error('Error getting trainer_id from localStorage:', e);
        }
      }
      
      if (trainerId) {
        setResolvedTrainerId(trainerId);
        const response = await getTrainerLocations(trainerId);
        setTrainerLocations(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error loading trainer locations:', error);
      setTrainerLocations([]);
    }
  };

  const handleAddLocation = async () => {
    const name = (newLocationName || '').trim();
    const address = (newLocationAddress || '').trim();

    if (!name) {
      toast.error(t('workout.err.gymNameRequired'));
      return;
    }

    const trainerId = resolvedTrainerId || workout?.trainer_id;
    if (!trainerId) {
      toast.error(t('workout.err.trainerForLocation'));
      return;
    }

    setIsAddingLocation(true);
    try {
      const resp = await createTrainerLocation({
        trainer_id: trainerId,
        name,
        address: address || undefined,
      });

      const created = resp?.data || resp;
      const createdId = created?.id;
      if (!createdId) {
        throw new Error(t('workout.err.createdGymId'));
      }

      await loadTrainerLocations();

      setLocationValue(createdId);
      setLocationType('offline');
      setShowAddLocationForm(false);
      setNewLocationName('');
      setNewLocationAddress('');

      toast.success(t('workout.toast.gymAdded'));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || t('workout.err.gymAddFailed');
      toast.error(msg);
    } finally {
      setIsAddingLocation(false);
    }
  };

  useEffect(() => {
    if (show && workout) {
      loadExercises();
      loadAllExercises();
      loadClients();
      loadTrainingPlans();
      loadPreviousWorkout();
      loadTrainerLocations();
      setExerciseForm({ name: '', sets: [] });
      setEditingExercise(null);
      setComment(workout.comment || '');
      setCommentText(workout.comment || '');
      setIsEditingComment(false);
      setShowAddExercise(false);
      setShowExerciseSelector(false);
      setSelectedExercise(null);
      setExerciseSearch('');
      setSelectedMuscleGroup(null);
      setShowPlanSelector(false);
      setSelectedPlan(null);
      setShowPlanSelectorInForm(false);
      setShowPlanAssignment(false);
      setWorkoutPlanId(workout.plan_id || null);
      setShowPreviousWorkout(false);
      setCurrentCompletedWorkoutIndex(0); // Reset navigation index when opening new workout
      // Initialize location state (только сохранённые залы / онлайн; произвольный текст в БД отображается в карточке, в select — «Не указано»)
      const w = workout;
      const isOnlineLoc =
        w.location === 'online' ||
        w.location === API_LOCATION_ONLINE_LABEL ||
        w.location_type === 'online';
      const workoutLocationType =
        w.location_type ||
        (isOnlineLoc ? 'online' : w.location_id ? 'offline' : null);

      if (w.location_id) {
        setLocationValue(w.location_id);
      } else if (workoutLocationType === 'online') {
        setLocationValue('online');
      } else {
        setLocationValue('');
      }
      setLocationType(workoutLocationType);
      setIsEditingLocation(false);
      setShowAddLocationForm(false);
      setNewLocationName('');
      setNewLocationAddress('');
    }
  }, [show, workout]);

  // Обработчик клика вне компонента для закрытия списка
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exerciseSelectorRef.current && !exerciseSelectorRef.current.contains(event.target)) {
        setShowExerciseSelector(false);
      }
    };

    if (showExerciseSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExerciseSelector]);

  // Function to load all clients
  const loadClients = async () => {
    try {
      // Try to get trainer_id from workout or localStorage
      let trainerId = null;
      if (workout?.trainer_id) {
        trainerId = workout.trainer_id;
      } else {
        try {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            trainerId = user.id;
          }
        } catch (e) {
          console.error('Error getting trainer_id from localStorage:', e);
        }
      }
      
      const response = await getClients(trainerId);
      setClients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  // Function to get client name from workout - using useMemo to recalculate when clients load
  const clientName = useMemo(() => {
    if (!workout) {
      return '';
    }
    
    // Try allClients first (for group workouts)
    if (workout.allClients && workout.allClients.length > 0) {
      const names = workout.allClients.map((c) => displayClientName(c?.name)).filter(Boolean);
      if (names.length > 0) {
        return names.join(', ');
      }
    }
    
    // Try direct client name (без никнейма в UI)
    if (workout.client_name || workout.client_nickname) {
      const formatted = displayClientName(workout.client_name);
      if (formatted) {
        return formatted;
      }
    }
    
    // Try to find client in loaded clients list by ID
    if (workout.client_id) {
      if (clients.length > 0) {
        const client = clients.find(c => c.id === workout.client_id);
        if (client) {
          const formatted = displayClientName(client.name);
          return formatted;
        } else {
          // If client not found but we have client_id, wait a bit for clients to load
          // Return empty for now, will update when clients load
          return '';
        }
      } else {
        // Clients not loaded yet, return empty for now
        return '';
      }
    }
    
    // Fallback - check if workout has any client-related fields we missed
    if (workout.client) {
      const formatted = displayClientName(workout.client.name);
      if (formatted) {
        return formatted;
      }
    }
    
    // Final fallback
    return '';
  }, [workout, clients]);

  // Function to handle opening client profile
  const handleOpenClientProfile = async () => {
    if (!workout) return;

    // Find the client by ID or nickname
    let client = null;

    if (workout.client_id) {
      // If workout has client_id, find by ID
      client = clients.find(c => String(c.id) === String(workout.client_id));
    } else if (workout.client_nickname) {
      // If no client_id but has nickname, find by nickname
      client = clients.find(c => c.nickname === workout.client_nickname);
    } else if (workout.allClients && workout.allClients.length > 0) {
      // For group workouts, use first client
      const firstClientId = workout.allClients[0].id;
      client = clients.find(c => String(c.id) === String(firstClientId));
    }

    if (client) {
      setSelectedClient(client);
      setShowClientProfile(true);
    } else {
      // Fallback 1: use embedded client info from workout (if present)
      const embeddedClient =
        (workout.allClients && workout.allClients.length > 0 && workout.allClients[0]) ||
        workout.client ||
        null;

      if (embeddedClient?.id) {
        setSelectedClient(embeddedClient);
        setShowClientProfile(true);

        // Try to enrich embedded client from API in background (non-blocking UX)
        try {
          const resp = await getUser(embeddedClient.id);
          if (resp?.data?.id) {
            setSelectedClient(resp.data);
          }
        } catch (e) {
          console.warn('[WorkoutDetailModal] getUser failed, using embedded client only', e);
        }
        return;
      }

      // Fallback 2: fetch by client_id directly if we have it
      const clientIdToFetch = workout.client_id || (workout.allClients?.[0]?.id ?? null);
      if (clientIdToFetch) {
        try {
          const resp = await getUser(clientIdToFetch);
          if (resp?.data?.id) {
            setSelectedClient(resp.data);
            setShowClientProfile(true);
            return;
          }
        } catch (e) {
          console.warn('[WorkoutDetailModal] getUser failed', e);
        }
      }

      console.warn('Client not found for workout (no fallback worked):', workout);
    }
  };

  const isGroupWorkout = Boolean(workout?.isGroup) || (Array.isArray(workout?.allClients) && workout.allClients.length > 1);

  const openClientPickerOrProfile = () => {
    if (isGroupWorkout) {
      setShowClientPicker(true);
      return;
    }
    handleOpenClientProfile();
  };

  const handleOpenWorkoutForClient = async (clientId) => {
    if (!clientId || !workout?.date || !workout?.time) return;

    try {
      setIsSwitchingClientWorkout(true);
      const resp = await getClientWorkouts(clientId);
      const list = Array.isArray(resp?.data) ? resp.data : [];

      const targetDate = String(workout.date || '');
      const targetTime = String(workout.time || '').slice(0, 5);
      const targetEnd = workout.end_time ? String(workout.end_time).slice(0, 5) : null;

      const match = list.find((w) => {
        const d = String(w?.date || '');
        const t = String(w?.time || '').slice(0, 5);
        const e = w?.end_time ? String(w.end_time).slice(0, 5) : null;
        if (!d || !t) return false;
        if (!d.startsWith(targetDate)) return false;
        if (t !== targetTime) return false;
        if (targetEnd && e && e !== targetEnd) return false;
        return true;
      });

      if (!match) {
        toast.error(t('workout.err.clientWorkoutNotFound'));
        return;
      }

      setShowClientPicker(false);
      if (typeof onOpenWorkout === 'function') {
        onOpenWorkout(match);
      } else {
        toast.error(t('workout.err.openClientWorkoutNoHandler'));
      }
    } catch (e) {
      console.error('[WorkoutDetailModal] Failed to open workout for selected client:', e);
      toast.error(t('workout.err.openClientWorkout'));
    } finally {
      setIsSwitchingClientWorkout(false);
    }
  };

  const getWorkoutMatchFromList = (list, target) => {
    try {
      const targetDate = String(target?.date || '');
      const targetTime = String(target?.time || '').slice(0, 5);
      const targetEnd = target?.end_time ? String(target.end_time).slice(0, 5) : null;
      if (!targetDate || !targetTime) return null;

      return (Array.isArray(list) ? list : []).find((w) => {
        const d = String(w?.date || '');
        const t = String(w?.time || '').slice(0, 5);
        const e = w?.end_time ? String(w.end_time).slice(0, 5) : null;
        if (!d || !t) return false;
        if (!d.startsWith(targetDate)) return false;
        if (t !== targetTime) return false;
        if (targetEnd && e && e !== targetEnd) return false;
        return true;
      }) || null;
    } catch (e) {
      return null;
    }
  };

  const ensureGroupSlotWorkoutIds = async () => {
    if (!isGroupWorkout) return {};
    const participants = (Array.isArray(workout?.allClients) ? workout.allClients : []).filter(c => c?.id);
    if (participants.length === 0) return {};

    // If already built for all participants, reuse.
    const existing = groupSlotWorkoutIdsByClientId || {};
    const missing = participants.filter(c => !existing[String(c.id)]);
    if (missing.length === 0) return existing;

    const updates = {};
    await Promise.all(missing.map(async (c) => {
      try {
        const resp = await getClientWorkouts(c.id);
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const match = getWorkoutMatchFromList(list, workout);
        if (match?.id) {
          updates[String(c.id)] = match.id;
        }
      } catch (e) {
        // ignore per-client failures
      }
    }));

    const merged = { ...existing, ...updates };
    setGroupSlotWorkoutIdsByClientId(merged);
    return merged;
  };

  useEffect(() => {
    if (!show || !isGroupWorkout) {
      setGroupSlotWorkoutIdsByClientId({});
      return;
    }
    // Preload mapping so "add exercise for all" is fast and reliable.
    ensureGroupSlotWorkoutIds();
  }, [show, isGroupWorkout, workout?.date, workout?.time, workout?.end_time, workout?.allClients]);

  const addExerciseToScope = async (exerciseData) => {
    if (!exerciseData) return;

    if (!isGroupWorkout) {
      // Individual workout: use parent hook (keeps existing behavior)
      return await onAddExercise(workout.id, exerciseData);
    }

    const map = await ensureGroupSlotWorkoutIds();
    const ids = Array.from(new Set(Object.values(map || {}).filter(Boolean)));
    if (ids.length === 0) {
      throw new Error(t('workout.err.groupExerciseTargets'));
    }

    let ok = 0;
    let fail = 0;
    for (const wid of ids) {
      try {
        await createExercise({ workout_id: wid, ...exerciseData });
        ok += 1;
      } catch (e) {
        fail += 1;
      }
    }

    if (onWorkoutUpdate) onWorkoutUpdate();
    // Refresh current workout exercises in UI (they should now be identical across participants)
    try { loadExercises(); } catch (_) {}

    if (fail > 0) {
      toast.error(t('workout.toast.exerciseAddedPartial', { ok, total: ids.length }));
    } else {
      toast.success(t('workout.toast.exerciseAddedAll', { n: ids.length }));
    }
  };


  // Function to load all exercises (standard + custom)
  const loadAllExercises = async () => {
    try {
      // Get trainer_id from workout or localStorage (same as other trainer-scoped requests)
      let trainerId = workout?.trainer_id || null;
      if (!trainerId) {
        try {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            trainerId = user?.id || null;
          }
        } catch (e) {
          console.error('Error getting trainer_id from localStorage:', e);
        }
      }
      if (!trainerId) trainerId = 'default-trainer';

      const response = await getAllExercises(trainerId);
      // De-duplicate by name (custom overrides standard if same name exists)
      const raw = Array.isArray(response.data) ? response.data : [];
      const sorted = [...raw].sort((a, b) => {
        const aType = a?.type === 'custom' ? 0 : 1;
        const bType = b?.type === 'custom' ? 0 : 1;
        return aType - bType;
      });
      const seen = new Set();
      const deduped = [];
      for (const ex of sorted) {
        const name = (ex?.label || ex?.value || ex?.name || '').trim().toLowerCase();
        if (!name) continue;
        if (seen.has(name)) continue;
        seen.add(name);
        deduped.push(ex);
      }
      setAllExercises(deduped);

      // Also load custom exercises separately for local state
      const customResponse = await getCustomExercises(trainerId);
      setCustomExercises(customResponse.data);
    } catch (error) {
      console.error('Error loading all exercises:', error);
    }
  };

  // Function to load training plans
  const loadTrainingPlans = async () => {
    try {
      // Get trainer_id from workout or localStorage
      let trainerId = null;
      if (workout?.trainer_id) {
        trainerId = workout.trainer_id;
      } else {
        try {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            trainerId = user.id;
          }
        } catch (e) {
          console.error('Error getting trainer_id from localStorage:', e);
        }
      }
      
      if (trainerId) {
        const response = await getTrainingPrograms(trainerId);
        setTrainingPlans(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error loading training plans:', error);
      setTrainingPlans([]);
    }
  };

  const loadExercises = async () => {
    if (!workout || !workout.id) {
      console.warn('[WorkoutDetailModal] loadExercises: no workout or workout.id');
      return [];
    }

    setLoading(true);
    try {
      const response = await getExercises(workout.id);
      const loadedExercises = response.data || [];
      setExercises(loadedExercises);
      return loadedExercises;
    } catch (error) {
      console.error('[WorkoutDetailModal] Error loading exercises:', error);
      setExercises([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Function to load previous workout
  const loadPreviousWorkout = async () => {
    if (!workout || !workout.client_id) {
      setPreviousWorkout(null);
      setPreviousWorkoutExercises([]);
      setCompletedWorkouts([]);
      setCurrentCompletedWorkoutIndex(0);
      return;
    }

    setLoadingPreviousWorkout(true);
    try {
      // Get all client workouts
      const response = await getClientWorkouts(workout.client_id);
      const workouts = response.data || [];
      
      // Find the current workout date and time
      const currentWorkoutDate = workout.date;
      const currentWorkoutTime = workout.time || '00:00';
      
      // Filter only completed workouts (status = 'completed' or has deduction)
      // Exclude cancelled and transferred workouts
      // Only workouts before current workout
      const completedWorkoutsList = workouts
        .filter(w => {
          if (!w.id || w.id === workout.id) return false;
          
          // Exclude cancelled and transferred
          if (w.status === 'cancelled' || w.status === 'transferred') return false;
          
          // Only include completed workouts
          if (w.status !== 'completed') {
            // Check if workout is in the past (completed by date/time)
            const workoutDate = w.date;
            const workoutTime = w.time || '00:00';
            
            // If date is in the past, consider it completed
            if (workoutDate < currentWorkoutDate) return true;
            
            // If same date, check if time has passed
            if (workoutDate === currentWorkoutDate && workoutTime < currentWorkoutTime) return true;
            
            return false;
          }
          
          // If status is 'completed', check if it's before current workout
          const workoutDate = w.date;
          const workoutTime = w.time || '00:00';
          
          // If date is earlier, it's previous
          if (workoutDate < currentWorkoutDate) return true;
          
          // If same date, check time
          if (workoutDate === currentWorkoutDate && workoutTime < currentWorkoutTime) return true;
          
          return false;
        })
        .sort((a, b) => {
          // Sort by date descending, then by time descending to get the most recent first
          const dateA = a.date;
          const dateB = b.date;
          const timeA = a.time || '00:00';
          const timeB = b.time || '00:00';
          
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;
          if (timeA > timeB) return -1;
          if (timeA < timeB) return 1;
          return 0;
        });
      
      setCompletedWorkouts(completedWorkoutsList);
      
      // Set the most recent completed workout (index 0)
      if (completedWorkoutsList.length > 0) {
        setCurrentCompletedWorkoutIndex(0);
        const previous = completedWorkoutsList[0];
        setPreviousWorkout(previous);
        // Load exercises for previous workout
        try {
          const exercisesResponse = await getExercises(previous.id);
          setPreviousWorkoutExercises(exercisesResponse.data || []);
        } catch (error) {
          console.error('Error loading previous workout exercises:', error);
          setPreviousWorkoutExercises([]);
        }
      } else {
        setPreviousWorkout(null);
        setPreviousWorkoutExercises([]);
        setCurrentCompletedWorkoutIndex(0);
      }
    } catch (error) {
      console.error('Error loading previous workout:', error);
      setPreviousWorkout(null);
      setPreviousWorkoutExercises([]);
      setCompletedWorkouts([]);
      setCurrentCompletedWorkoutIndex(0);
    } finally {
      setLoadingPreviousWorkout(false);
    }
  };

  // Navigate to previous completed workout (earlier in time)
  const navigateToPreviousCompletedWorkout = async () => {
    if (currentCompletedWorkoutIndex < completedWorkouts.length - 1) {
      const newIndex = currentCompletedWorkoutIndex + 1;
      setCurrentCompletedWorkoutIndex(newIndex);
      const workout = completedWorkouts[newIndex];
      setPreviousWorkout(workout);
      
      // Load exercises for this workout
      setLoadingPreviousWorkout(true);
      try {
        const exercisesResponse = await getExercises(workout.id);
        setPreviousWorkoutExercises(exercisesResponse.data || []);
      } catch (error) {
        console.error('Error loading previous workout exercises:', error);
        setPreviousWorkoutExercises([]);
      } finally {
        setLoadingPreviousWorkout(false);
      }
    }
  };

  // Navigate to next completed workout (later in time)
  const navigateToNextCompletedWorkout = async () => {
    if (currentCompletedWorkoutIndex > 0) {
      const newIndex = currentCompletedWorkoutIndex - 1;
      setCurrentCompletedWorkoutIndex(newIndex);
      const workout = completedWorkouts[newIndex];
      setPreviousWorkout(workout);
      
      // Load exercises for this workout
      setLoadingPreviousWorkout(true);
      try {
        const exercisesResponse = await getExercises(workout.id);
        setPreviousWorkoutExercises(exercisesResponse.data || []);
      } catch (error) {
        console.error('Error loading previous workout exercises:', error);
        setPreviousWorkoutExercises([]);
      } finally {
        setLoadingPreviousWorkout(false);
      }
    }
  };


  const handleAddSet = () => {
    // Get the last set's values for prefilling
    const lastSet = exerciseForm.sets[exerciseForm.sets.length - 1];
    const newSet = lastSet ? {
      reps: lastSet.reps || '',
      weight: lastSet.weight || ''
    } : { reps: '', weight: '' };

    setExerciseForm({
      ...exerciseForm,
      sets: [...exerciseForm.sets, newSet]
    });
  };

  // Filter exercises for inline selector (search by localized label or canonical value)
  const filteredExercises = localizedAllExercises.filter(ex => {
    const label = (ex.label || '').toLowerCase();
    const val = (ex.value || '').toLowerCase();
    const query = (exerciseSearch || exerciseForm?.name || '').trim().toLowerCase();
    const matchesSearch = !query || label.includes(query) || val.includes(query);
    const matchesMuscleGroup = !selectedMuscleGroup || 
      (ex.muscle_group === selectedMuscleGroup) ||
      (ex.type === 'standard' && !selectedMuscleGroup);
    return matchesSearch && matchesMuscleGroup;
  }).slice(0, 50);

  // Function to filter exercises based on input
  const handleExerciseNameChange = (value) => {
    setExerciseForm({ ...exerciseForm, name: value });

    if (value.trim()) {
      const q = value.toLowerCase();
      const filtered = localizedAllExercises
        .filter(
          (exercise) =>
            (exercise.value || '').toLowerCase().includes(q) ||
            (exercise.label || '').toLowerCase().includes(q)
        )
        .map(exercise => exercise.value); // Canonical names for API

      // Проверяем, есть ли точное совпадение в списке упражнений
      const trim = value.trim().toLowerCase();
      const exactMatch = localizedAllExercises.some(
        (exercise) =>
          (exercise.value || '').toLowerCase() === trim ||
          (exercise.label || '').toLowerCase() === trim
      );

      // Если нет точного совпадения, добавляем предложение создать новое упражнение
      if (!exactMatch && value.trim().length > 2) {
        const suggestions = [...filtered.slice(0, 4)]; // Ограничиваем до 4 существующих
        suggestions.push(buildSuggestAddToDict(value));
        setExerciseSuggestions(suggestions);
      } else {
        setExerciseSuggestions(filtered.slice(0, 5)); // Limit to 5 suggestions
      }
      
      setShowExerciseDropdown(true);

      // Check for duplicates as user types
      const existingExercise = exercises.find(exercise =>
        exercise.name.toLowerCase() === value.trim().toLowerCase()
      );

      if (existingExercise) {
        setDuplicateExerciseName(value.trim());
        setShowDuplicateAlert(true);
      } else {
        setShowDuplicateAlert(false);
      }
    } else {
      setExerciseSuggestions([]);
      setShowExerciseDropdown(false);
      setShowDuplicateAlert(false);
    }
  };

  // Function to select exercise from dropdown
  const selectExerciseSuggestion = (exercise) => {
    // Проверяем, является ли это предложением добавить новое упражнение
    const parsedSuggest = parseSuggestAddToDict(exercise);
    if (parsedSuggest != null) {
      const exerciseName = parsedSuggest;
      setExerciseForm({ ...exerciseForm, name: exerciseName });
      
      // Показываем диалог подтверждения для добавления нового упражнения
      setPendingExerciseName(exerciseName);
      setShowExerciseConfirmDialog(true);
    } else {
      setExerciseForm({ ...exerciseForm, name: exercise });
    }
    
    setExerciseSuggestions([]);
    setShowExerciseDropdown(false);

    // Check for duplicates when selecting from dropdown
    const existingExercise = exercises.find(ex =>
      ex.name.toLowerCase() === exercise.trim().toLowerCase()
    );

    if (existingExercise) {
      setDuplicateExerciseName(exercise.trim());
      setShowDuplicateAlert(true);
    } else {
      setShowDuplicateAlert(false);
    }
  };

  // Function to save new exercise to trainer's dictionary
  const saveNewExercise = async (exerciseName, muscleGroup) => {
    try {
      // Get trainer_id from workout or localStorage
      let trainerId = workout?.trainer_id || null;
      if (!trainerId) {
        try {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            trainerId = user?.id || null;
          }
        } catch (e) {
          console.error('Error getting trainer_id from localStorage:', e);
        }
      }
      if (!trainerId) trainerId = 'default-trainer';

      await createCustomExercise({
        name: exerciseName.trim(),
        trainer_id: trainerId,
        muscle_group: muscleGroup || 'other'
      });

      // Update local state to include the new exercise
      setCustomExercises(prev => [...prev, {
        id: Date.now(), // Temporary ID until refresh
        name: exerciseName.trim(),
        trainer_id: trainerId,
        muscle_group: muscleGroup || 'other'
      }]);

      // Refresh all exercises to sync across components
      await loadAllExercises();

      // Trigger refresh in parent components
      if (onExerciseUpdate) onExerciseUpdate();

    } catch (error) {
      console.error('Error saving new exercise:', error);
      if (error.response?.status === 409) {
        console.error('Упражнение уже есть в словаре');
      } else {
        console.error('Ошибка при сохранении упражнения');
      }
    }
  };

  // Function to handle exercise confirmation
  const handleExerciseConfirmation = async (exerciseName, action) => {
    setPendingExerciseName(exerciseName);
    setPendingExerciseAction(() => action);
    setShowExerciseConfirmDialog(true);
  };

  // Function to confirm saving exercise to dictionary
  const confirmSaveExercise = async () => {
    if (pendingExerciseAction) {
      await saveNewExercise(pendingExerciseName, pendingMuscleGroup);
      await pendingExerciseAction();
    } else {
      // Если нет pendingExerciseAction, просто сохраняем упражнение в словарь
      await saveNewExercise(pendingExerciseName, pendingMuscleGroup);
    }
    setShowExerciseConfirmDialog(false);
    setPendingExerciseName('');
    setPendingExerciseAction(null);
  };

  // Function to cancel exercise save
  const cancelSaveExercise = async () => {
    if (pendingExerciseAction) {
      await pendingExerciseAction();
    }
    setShowExerciseConfirmDialog(false);
    setPendingExerciseName('');
    setPendingExerciseAction(null);
  };

  const handleRemoveSet = (index) => {
    // Remove set directly without confirmation dialog
    setExerciseForm({
      ...exerciseForm,
      sets: exerciseForm.sets.filter((_, i) => i !== index)
    });
  };

  // Removed confirmRemoveSet and cancelRemoveSet functions as we now delete directly

  const handleSetChange = (index, field, value) => {
    // Валидация для повторений (только целые числа) и веса (десятичные числа)
    if (field === 'reps') {
      // Разрешаем только целые числа для повторений
      if (value === '' || /^\d+$/.test(value)) {
        const newSets = [...exerciseForm.sets];
        newSets[index][field] = value;
        setExerciseForm({
          ...exerciseForm,
          sets: newSets
        });
      }
    } else if (field === 'weight') {
      // Разрешаем десятичные числа для веса (запятая или точка)
      if (value === '' || /^\d+([.,]\d+)?$/.test(value)) {
        const newSets = [...exerciseForm.sets];
        newSets[index][field] = value;
        setExerciseForm({
          ...exerciseForm,
          sets: newSets
        });
      }
    } else {
      // Для других полей сохраняем текущую логику
      const newSets = [...exerciseForm.sets];
      newSets[index][field] = value;
      setExerciseForm({
        ...exerciseForm,
        sets: newSets
      });
    }
  };

  // Function to focus input field when icon is clicked
  const focusInput = (setIndex, fieldName) => {
    const repsPh = t('plan.repsPlaceholder');
    const wPh = t('workout.weightInputShort');
    const inputSelector = fieldName === 'reps' ? `input[placeholder="${repsPh}"]` : `input[placeholder="${wPh}"]`;
    const inputs = document.querySelectorAll(inputSelector);
    if (inputs[setIndex]) {
      inputs[setIndex].focus();
      inputs[setIndex].select();
    }
  };



  const handleAddExercise = async (e) => {
    e.preventDefault();

    // Validate exercise name
    if (!exerciseForm.name.trim()) {
      alert(t('workout.alert.pickExerciseName'));
      return;
    }

    // Check if exercise already exists in current workout
    const existingExercise = exercises.find(exercise =>
      exercise.name.toLowerCase() === exerciseForm.name.trim().toLowerCase()
    );

    if (existingExercise) {
      // Alert is already shown, but we can still proceed with adding
      // The user can choose to add the duplicate if they want
    }

    // Validate sets data
    if (exerciseForm.sets.length > 0) {
      for (let i = 0; i < exerciseForm.sets.length; i++) {
        const set = exerciseForm.sets[i];
        const setNumber = i + 1;

        // Check if reps is missing or invalid (только целые числа)
        if (!set.reps || set.reps === '' || !/^\d+$/.test(set.reps) || parseInt(set.reps) <= 0) {
          alert(t('workout.alert.setRepsInvalid', { n: setNumber }));
          return;
        }

        // Check if weight is invalid (десятичные числа, если указан); weight с API может быть числом
        const weightStr = set.weight == null || set.weight === '' ? '' : String(set.weight).trim();
        if (weightStr !== '' && (!/^\d+([.,]\d+)?$/.test(weightStr) || parseFloat(weightStr.replace(',', '.')) < 0)) {
          alert(t('workout.alert.setWeightInvalid', { n: setNumber }));
          return;
        }
      }
    }

    // Check if exercise is new and offer to save it
    const isNewExercise = !allExercises.some(exercise =>
      exercise.value.toLowerCase() === exerciseForm.name.trim().toLowerCase()
    );

    if (isNewExercise) {
      // Store the exercise addition action to execute after confirmation
      const addExerciseAction = async () => {
        setIsAddingExercise(true);
        try {
        await addExerciseToScope(exerciseForm);
        setExerciseForm({ name: '', sets: [] });
        setShowAddExercise(false);
        setShowExerciseSelector(false);
        setSelectedExercise(null);
        setExerciseSearch('');
        setSelectedMuscleGroup(null);
        loadExercises();
        setSuccessMessage(t('workout.success.exerciseAdded'));
        setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
          console.error('[WorkoutDetailModal] Error adding exercise:', error);
          const message =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            t('workout.err.exerciseAdd');
          alert(message);
        } finally {
          setIsAddingExercise(false);
        }
      };

      await handleExerciseConfirmation(exerciseForm.name.trim(), addExerciseAction);
      return; // Exit early, the action will be executed after confirmation
    }

    setIsAddingExercise(true);
    try {
    await addExerciseToScope(exerciseForm);
    setExerciseForm({ name: '', sets: [{ reps: '', weight: '' }] });
    setShowAddExercise(false);
    setShowExerciseSelector(false);
    setSelectedExercise(null);
    setExerciseSearch('');
    setSelectedMuscleGroup(null);
    loadExercises();
    setSuccessMessage(t('workout.success.exerciseAdded'));
    setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[WorkoutDetailModal] Error adding exercise:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        t('workout.err.exerciseAdd');
      alert(message);
    } finally {
      setIsAddingExercise(false);
    }
  };

  const handleEditExercise = (exercise) => {
    setEditingExercise(exercise);
    setExerciseForm({
      name: exercise.name,
      sets: exercise.sets || []
    });
    setShowAddExercise(true);
    setShowExerciseSelector(false);
  };

  // Function to handle adding exercise from inline selector
  const handleSelectExercise = (exercise) => {
    const canonical = (exercise.value || exercise.name || '').trim();
    setSelectedExercise(exercise);
    setExerciseForm({ ...exerciseForm, name: canonical || (exercise.label || '').trim() });
    setShowExerciseSelector(false);
    setExerciseSearch('');
    setSelectedMuscleGroup(null);
  };

  // Function to add training plan exercises to workout (replaces existing)
  const handleAddPlanExercises = async (plan, closeForm = false) => {
    if (!plan || !plan.exercises || plan.exercises.length === 0) {
      alert(t('workout.alert.planNoExercises'));
      return;
    }

    try {
      // Удаляем все существующие упражнения, если заменяем план
      if (workoutPlanId) {
        // Можно добавить подтверждение, но пока просто заменяем
      }

      if (!isGroupWorkout) {
        for (const planExercise of plan.exercises) {
          const exerciseData = {
            name: planExercise.exercise_name || planExercise.name,
            sets: planExercise.default_sets || planExercise.sets || []
          };
          await addExerciseToScope(exerciseData);
        }

        // Обновляем plan_id в тренировке
        if (workout && workout.id) {
          try {
            await updateWorkout(workout.id, { plan_id: plan.id });
            setWorkoutPlanId(plan.id);
          } catch (error) {
            console.error('Error updating workout plan_id:', error);
          }
        }
      } else {
        const map = await ensureGroupSlotWorkoutIds();
        const ids = Array.from(new Set(Object.values(map || {}).filter(Boolean)));
        if (ids.length === 0) {
          throw new Error(t('workout.err.participantWorkoutsPlan'));
        }

        for (const planExercise of plan.exercises) {
          const exerciseData = {
            name: planExercise.exercise_name || planExercise.name,
            sets: planExercise.default_sets || planExercise.sets || []
          };
          for (const wid of ids) {
            await createExercise({ workout_id: wid, ...exerciseData });
          }
        }

        await Promise.all(ids.map(async (wid) => {
          try {
            await updateWorkout(wid, { plan_id: plan.id });
          } catch (e) {
            // ignore
          }
        }));

        setWorkoutPlanId(plan.id);
        if (onWorkoutUpdate) onWorkoutUpdate();
        try { loadExercises(); } catch (_) {}
      }

      loadExercises();
      setShowPlanSelector(false);
      setSelectedPlan(null);
      setShowPlanSelectorInForm(false);
      setShowPlanAssignment(false);
      if (closeForm) {
        setShowAddExercise(false);
      }
      setSuccessMessage(t('workout.success.planExercisesAdded', { n: plan.exercises.length, name: plan.name }));
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error adding plan exercises:', error);
      alert(t('workout.err.planExercisesAdd'));
    }
  };

  // Function to add plan exercises to existing exercises (doesn't replace)
  const handleAddPlanExercisesToExisting = async (plan) => {
    if (!plan || !plan.exercises || plan.exercises.length === 0) {
      console.warn('[WorkoutDetailModal] Plan has no exercises:', plan);
      alert(t('workout.alert.planNoExercises'));
      return;
    }

    // Собираем уже существующие упражнения, чтобы не ловить 409 на дубликатах
    const existingNames = new Set(
      (exercises || []).map(ex => (ex.name || '').trim().toLowerCase())
    );

    try {
      // Подготавливаем упражнения к добавлению, пропуская дубликаты
      const exercisesToAdd = plan.exercises
        .map((planExercise, index) => {
          // Парсим sets если это строка
          let sets = planExercise.default_sets || planExercise.sets || [];
          if (typeof sets === 'string') {
            try {
              sets = JSON.parse(sets);
            } catch (e) {
              console.error('Ошибка парсинга sets:', e);
              sets = [];
            }
          }
          
          // Если sets пустой массив, оставляем пустым (необязательное поле)
          if (!Array.isArray(sets) || sets.length === 0) {
            sets = [];
          } else {
            // Убеждаемся, что каждый сет имеет правильную структуру для API
            // API требует reps как целое число > 0, weight как число или null
            sets = sets
              .filter(set => {
                // Фильтруем только валидные сеты (с reps)
                const reps = set.reps !== undefined && set.reps !== null && set.reps !== '' 
                  ? parseInt(set.reps) 
                  : null;
                return reps !== null && !isNaN(reps) && reps > 0;
              })
              .map(set => {
                const reps = parseInt(set.reps);
                const weight = set.weight !== undefined && set.weight !== null && set.weight !== '' 
                  ? parseFloat(set.weight) 
                  : null;
                return {
                  reps: reps,
                  weight: isNaN(weight) ? null : weight
                };
              });
          }

          const exerciseName = (planExercise.exercise_name || planExercise.name || '').trim();
          if (!exerciseName) {
            console.warn('[WorkoutDetailModal] Skip exercise without name at index', index);
            return null;
          }

          // Пропускаем дубликаты
          if (existingNames.has(exerciseName.toLowerCase())) {
            return null;
          }

          // Помечаем, чтобы не добавлять одинаковые в рамках одной операции
          existingNames.add(exerciseName.toLowerCase());

          const prepared = { name: exerciseName, sets };
          return prepared;
        })
        .filter(Boolean);

      if (exercisesToAdd.length === 0) {
        alert(t('workout.alert.planExercisesAllDuplicate'));
        return;
      }

      // Добавляем все упражнения параллельно; ошибки по отдельным упражнениям не прерывают процесс
      const addResults = await Promise.all(
        exercisesToAdd.map((exerciseData, index) =>
          onAddExercise(workout.id, exerciseData)
            .then((res) => ({ ok: true, res }))
            .catch((error) => {
              // 409 — дубликат, пропускаем молча
              if (error?.response?.status === 409) {
                console.warn(`[WorkoutDetailModal] Duplicate exercise skipped (${exerciseData.name})`);
                return { ok: false, skipped: true, error };
              }
              console.error(`[WorkoutDetailModal] Error adding exercise ${index}:`, error);
              return { ok: false, skipped: false, error };
            })
        )
      );

      const successCount = addResults.filter(r => r.ok).length;
      const failedNonSkipped = addResults.filter(r => r.ok === false && !r.skipped).length;

      // Моментально обновляем UI
      setShowPlanSelector(false);
      setSelectedPlan(null);
      
      // Небольшая задержка перед перезагрузкой, чтобы дать время API обработать запросы
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Перезагружаем упражнения для обновления списка
      const loadedExercises = await loadExercises();
      
      // Дополнительная проверка: если упражнения не загрузились, попробуем еще раз
      if (loadedExercises.length === 0 && exercisesToAdd.length > 0) {
        console.warn('[WorkoutDetailModal] No exercises loaded, retrying...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        const retryExercises = await loadExercises();
      }

      if (successCount > 0) {
        setSuccessMessage(t('workout.success.planExercisesAddedCount', { n: successCount, name: plan.name }));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else if (failedNonSkipped > 0) {
        alert(t('workout.alert.planExercisesAddFail'));
      } else {
        alert(t('workout.alert.planExercisesAllDup2'));
      }
      
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('[WorkoutDetailModal] Error adding plan exercises:', error);
      console.error('[WorkoutDetailModal] Error details:', {
        message: error.message,
        stack: error.stack,
        workoutId: workout?.id,
        plan: plan
      });
      alert(t('workout.err.planExercisesWithMsg', { msg: error.message || t('workout.err.unknown') }));
    }
  };

  // Function to handle plan selection from PlanAssignmentModal
  const handlePlanSelectFromModal = async (plan) => {
    // If replacing a plan, delete all existing exercises first
    if (workoutPlanId && exercises.length > 0) {
      if (!window.confirm(t('workout.confirm.replacePlanExercises'))) {
        return;
      }
      
      // Delete all existing exercises
      try {
        for (const exercise of exercises) {
          await deleteExercise(exercise.id);
        }
      } catch (error) {
        console.error('Error deleting existing exercises:', error);
        alert(t('workout.err.deleteExistingExercises'));
        return;
      }
    }
    
    await handleAddPlanExercises(plan, false);
  };

  // Function to handle manual exercise creation from PlanAssignmentModal
  const handleManualExerciseCreate = async (exercises) => {
    // Удаляем все существующие упражнения
    // Можно добавить подтверждение
    
    // Добавляем новые упражнения
    try {
      for (const exercise of exercises) {
        const exerciseData = {
          name: exercise.name,
          sets: exercise.sets || []
        };
        await onAddExercise(workout.id, exerciseData);
      }
      
      // Убираем plan_id из тренировки
      if (workout && workout.id) {
        try {
          await updateWorkout(workout.id, { plan_id: null });
          setWorkoutPlanId(null);
        } catch (error) {
          console.error('Error updating workout plan_id:', error);
        }
      }

      loadExercises();
      setShowPlanAssignment(false);
      setSuccessMessage(t('workout.success.addedNExercises', { n: exercises.length }));
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error adding exercises:', error);
      alert(t('workout.err.addExercisesBulk'));
    }
  };

  const handleUpdateExercise = async (e) => {
    e.preventDefault();

    // Validate exercise name
    if (!exerciseForm.name.trim()) {
      alert(t('workout.alert.enterExerciseName'));
      return;
    }

    // Check if exercise name conflicts with other exercises in current workout
    const nameChanged = editingExercise.name !== exerciseForm.name.trim();
    if (nameChanged) {
      const conflictingExercise = exercises.find(exercise =>
        exercise.id !== editingExercise.id &&
        exercise.name.toLowerCase() === exerciseForm.name.trim().toLowerCase()
      );

      if (conflictingExercise) {
        alert(t('workout.alert.duplicateExerciseInWorkout'));
        return;
      }
    }

    // Validate sets data
    if (exerciseForm.sets.length > 0) {
      for (let i = 0; i < exerciseForm.sets.length; i++) {
        const set = exerciseForm.sets[i];
        const setNumber = i + 1;

        // Check if reps is missing or invalid (только целые числа)
        if (!set.reps || set.reps === '' || !/^\d+$/.test(set.reps) || parseInt(set.reps) <= 0) {
          alert(t('workout.alert.setRepsInvalid', { n: setNumber }));
          return;
        }

        // Check if weight is invalid (десятичные числа, если указан); weight с API может быть числом
        const weightStr = set.weight == null || set.weight === '' ? '' : String(set.weight).trim();
        if (weightStr !== '' && (!/^\d+([.,]\d+)?$/.test(weightStr) || parseFloat(weightStr.replace(',', '.')) < 0)) {
          alert(t('workout.alert.setWeightInvalid', { n: setNumber }));
          return;
        }
      }
    }

    // Check if exercise is new (not in dictionary)
    const isNewExercise = nameChanged && !allExercises.some(exercise =>
      exercise.value.toLowerCase() === exerciseForm.name.trim().toLowerCase()
    );

    // For editing mode, if the new name doesn't exist in dictionary, offer to save it
    if (isNewExercise) {
      // Store the exercise update action to execute after confirmation
      const updateExerciseAction = async () => {
        try {
          await updateExercise(editingExercise.id, exerciseForm);
          setExerciseForm({ name: '', sets: [] });
          setEditingExercise(null);
          setShowAddExercise(false);
          loadExercises();
          setSuccessMessage(t('workout.success.exerciseUpdated'));
          setTimeout(() => setSuccessMessage(''), 3000);
          // Notify parent to refresh workout exercises (for muscle group badges)
          if (onWorkoutUpdate) {
            onWorkoutUpdate();
          }
        } catch (error) {
          console.error('Error updating exercise:', error);
          alert(t('workout.err.exerciseUpdate'));
        }
      };

      await handleExerciseConfirmation(exerciseForm.name.trim(), updateExerciseAction);
      return; // Exit early, the action will be executed after confirmation
    }

    try {
      await updateExercise(editingExercise.id, exerciseForm);
      setExerciseForm({ name: '', sets: [] });
      setEditingExercise(null);
      setShowAddExercise(false);
      loadExercises();
      setSuccessMessage(t('workout.success.exerciseUpdated'));
      setTimeout(() => setSuccessMessage(''), 3000);
      // Notify parent to refresh workout exercises (for muscle group badges)
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
      alert(t('workout.err.exerciseUpdate'));
    }
  };

  const handleDeleteExercise = (exerciseId) => {
    setExerciseToDelete(exerciseId);
    setShowExerciseDeleteDialog(true);
  };

  const confirmDeleteExercise = async () => {
    if (exerciseToDelete) {
      try {
        await deleteExercise(exerciseToDelete);
        loadExercises();
        setShowExerciseDeleteDialog(false);
        setExerciseToDelete(null);
        // Notify parent to refresh workout exercises (for muscle group badges)
        if (onWorkoutUpdate) {
          onWorkoutUpdate();
        }
      } catch (error) {
        console.error('Error deleting exercise:', error);
        alert(t('workout.err.exerciseDelete'));
      }
    }
  };

  const cancelDeleteExercise = () => {
    setShowExerciseDeleteDialog(false);
    setExerciseToDelete(null);
  };



  const handleCancelWorkout = () => {
    setShowCancelWorkoutDialog(true);
  };

  useEffect(() => {
    if (!showCancelWorkoutDialog) return;
    if (!workout?.client_id || !workout?.date) {
      setCanCancelFutureInSeries(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await getClientWorkouts(workout.client_id);
        const all = Array.isArray(resp?.data) ? resp.data : [];

        const baseDate = workout.date;
        const baseTime = workout.time || '';

        const isSubsequent = (w) => {
          if (!w?.date) return false;
          const t = w.time || '';
          if (w.date > baseDate) return true;
          if (w.date === baseDate) return t > baseTime;
          return false;
        };

        const hasFuture = all.some((w) => {
          if (!w?.id || w.id === workout.id) return false;
          if (w.status === 'cancelled' || w.status === 'transferred' || w.status === 'completed') return false;
          return isSubsequent(w);
        });

        if (!cancelled) setCanCancelFutureInSeries(Boolean(hasFuture));
      } catch (e) {
        console.warn('[WorkoutDetailModal] Failed to check future workouts for cancel scope:', e);
        if (!cancelled) setCanCancelFutureInSeries(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showCancelWorkoutDialog, workout?.id, workout?.client_id, workout?.date, workout?.time]);

  const handleSkipWorkout = () => {
    setShowSkipWorkoutDialog(true);
  };

  const isWorkoutInSameSeries = (candidateWorkout, baseWorkout) => {
    if (!candidateWorkout || !baseWorkout) return false;
    if (candidateWorkout.client_id !== baseWorkout.client_id) return false;
    if (candidateWorkout.time !== baseWorkout.time) return false;
    try {
      return new Date(candidateWorkout.date).getDay() === new Date(baseWorkout.date).getDay();
    } catch (e) {
      return false;
    }
  };

  const cancelWorkoutByScope = async (scope) => {
    if (isCancelling) return;
    if (!workout?.id) return;

    const workoutId = workout.id;
    const clientId = workout.client_id;
    const baseDate = workout.date;
    const baseTime = workout.time || '';

    try {
      setIsCancelling(true);

      if (scope === 'future') {
        if (!clientId) {
          toast.error(t('workout.err.cancelSeriesNoClient'));
          return;
        }

        const resp = await getClientWorkouts(clientId);
        const allWorkouts = Array.isArray(resp?.data) ? resp.data : [];

        const candidates = allWorkouts.filter((w) => {
          if (!w?.id || w.id === workoutId) return false;
          if (w.status === 'cancelled' || w.status === 'transferred' || w.status === 'completed') return false;
          if (!w.date) return false;
          const t = w.time || '';
          return w.date > baseDate || (w.date === baseDate && t >= baseTime);
        });

        const idsToCancel = Array.from(new Set([workoutId, ...candidates.map((w) => w.id)]));

        try {
          await bulkCancelWorkouts(idsToCancel);
          const n = idsToCancel.length;
          setShowCancelWorkoutDialog(false);
          if (n > 1) {
            toast.success(t('workout.toast.cancelledN', { n }));
          } else {
            toast.success(t('workout.toast.workoutCancelled'));
          }
        } catch (bulkErr) {
          console.warn('[WorkoutDetailModal] bulk-cancel failed, falling back to per-workout:', bulkErr);
          let okCount = 0;
          let failCount = 0;
          for (const wid of idsToCancel) {
            try {
              await updateWorkout(wid, { status: 'cancelled' });
              okCount += 1;
            } catch (e) {
              console.warn('[WorkoutDetailModal] Failed to cancel workout:', wid, e);
              failCount += 1;
            }
          }
          setShowCancelWorkoutDialog(false);
          if (okCount > 1) {
            toast.success(t('workout.toast.cancelledN', { n: okCount }));
          } else if (okCount === 1) {
            toast.success(t('workout.toast.workoutCancelled'));
          }
          if (failCount > 0) {
            toast.error(t('workout.err.cancelFailedN', { n: failCount }));
          }
        }
      } else {
        const response = await updateWorkout(workoutId, { status: 'cancelled' });
        const isSuccess =
          response?.status === 200 ||
          response?.statusText === 'OK' ||
          response?.data?.status === 'cancelled' ||
          response?.data?.id;

        if (!isSuccess) {
          throw new Error(t('workout.err.cancelNoConfirm'));
        }

        setShowCancelWorkoutDialog(false);
        toast.success(t('workout.toast.workoutCancelled'));
      }

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      onHide();
    } catch (error) {
      console.error('Error cancelling workout:', error);

      let errorMessage = t('workout.err.cancelWorkout');
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = t('workout.err.withMessage', { msg: error.message });
      }
      toast.error(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const confirmCancelWorkout = async () => cancelWorkoutByScope('single');
  const confirmCancelWorkoutAndFuture = async () => cancelWorkoutByScope('future');

  const confirmSkipWorkout = async () => {
    if (isSkipping) return;
    try {
      setIsSkipping(true);
      setShowSkipWorkoutDialog(false);
      const response = await updateWorkout(workout.id, { status: 'skipped' });
      const isSuccess = response?.status === 200 || response?.statusText === 'OK' || response?.data?.status === 'skipped' || response?.data?.id;
      if (isSuccess) {
        if (onWorkoutUpdate) onWorkoutUpdate();
        onHide();
      } else {
        setShowSkipWorkoutDialog(true);
        throw new Error(t('workout.err.markSkipped'));
      }
    } catch (error) {
      console.error('Error skipping workout:', error);
      setShowSkipWorkoutDialog(true);
      alert(error.message || t('workout.err.skipMark'));
    } finally {
      setIsSkipping(false);
    }
  };

  const cancelSkipWorkout = () => {
    setShowSkipWorkoutDialog(false);
  };

  const cancelCancelWorkout = () => {
    setShowCancelWorkoutDialog(false);
  };


  const handleRestoreWorkout = () => {
    setShowRestoreWorkoutDialog(true);
  };

  const confirmRestoreWorkout = async () => {
    try {
      setIsRestoring(true);
      await updateWorkout(workout.id, { status: 'scheduled' });
      setShowRestoreWorkoutDialog(false);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      onHide();
    } catch (error) {
      console.error('Error restoring workout:', error);
      setShowRestoreWorkoutDialog(false);
      alert(t('workout.err.restoreWorkout'));
    } finally {
      setIsRestoring(false);
    }
  };

  const handleSaveLocation = async () => {
    if (!workout || !workout.id) return;
    
    setIsUpdatingLocation(true);
    try {
      let locationData;
      if (locationType === 'online') {
        locationData = {
          location: API_LOCATION_ONLINE_LABEL,
          location_type: 'online',
          location_id: null,
        };
      } else if (locationType === 'offline') {
        if (locationValue && locationValue !== 'online') {
          const found = trainerLocations.find((l) => l.id === locationValue);
          locationData = {
            location: found?.name || null,
            location_type: 'offline',
            location_id: locationValue,
          };
        } else {
          locationData = {
            location: null,
            location_type: null,
            location_id: null,
          };
        }
      } else {
        locationData = {
          location: null,
          location_type: null,
          location_id: null,
        };
      }

      await updateWorkout(workout.id, locationData);
      setIsEditingLocation(false);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error updating location:', error);
      alert(t('workout.err.locationUpdate'));
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!workout || !workout.id) return;

    setIsUpdatingLocation(true);
    try {
      await updateWorkout(workout.id, { 
        location: null,
        location_type: null,
        location_id: null
      });
      setLocationValue('');
      setLocationType(null);
      setIsEditingLocation(false);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      alert(t('workout.err.locationDelete'));
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handleSaveComment = async () => {
    if (!workout || !workout.id) return;

    setIsUpdatingComment(true);
    try {
      const trimmedComment = commentText.trim();
      await updateWorkout(workout.id, { comment: trimmedComment || null });
      setComment(trimmedComment || null);
      setIsEditingComment(false);
      setSuccessMessage(trimmedComment ? t('workout.success.commentSaved') : t('workout.success.commentRemoved'));
      setTimeout(() => setSuccessMessage(''), 3000);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving comment:', error);
      alert(t('workout.err.commentSave'));
    } finally {
      setIsUpdatingComment(false);
    }
  };

  const handleEditComment = () => {
    setCommentText(comment == null ? '' : String(comment));
    setIsEditingComment(true);
  };

  const handleDeleteComment = async () => {
    if (!workout || !workout.id) return;

    setIsUpdatingComment(true);
    try {
      await updateWorkout(workout.id, { comment: null });
      setComment('');
      setCommentText('');
      setIsEditingComment(false);
      setSuccessMessage(t('workout.success.commentRemoved'));
      setTimeout(() => setSuccessMessage(''), 3000);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert(t('workout.err.commentDelete'));
    } finally {
      setIsUpdatingComment(false);
    }
  };

  const handleCancelEditComment = () => {
    setCommentText(comment == null ? '' : String(comment));
    setIsEditingComment(false);
  };

  // Helper functions for formatting
  const formatTime = (time) => {
    if (!time) return '';
    return time;
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      // Prefer parsing a plain 'YYYY-MM-DD' string as a local date. Using
      // `new Date('2026-04-22')` interprets it as UTC midnight, which shifts
      // the displayed day in any timezone west of UTC (e.g. 04/22 -> 04/21).
      const ymd = typeof date === 'string'
        ? date.match(/^(\d{4})-(\d{2})-(\d{2})/)
        : null;
      const workoutDate = ymd
        ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        : new Date(date);
      return workoutDate.toLocaleDateString(dateLocaleTag, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return date;
    }
  };

  // Function to validate reschedule conflicts in real-time
  const validateRescheduleConflictsRealtime = async (rescheduleData) => {
    try {
      const response = await validateWorkoutConflict({
        date: rescheduleData.date,
        time: rescheduleData.time,
        end_time: rescheduleData.endTime,
        client_id: workout.client_id,
        exclude_workout_id: workout.id
      });

      if (response.data.hasConflicts) {
        setRescheduleConflictingWorkouts(response.data.conflicts);
        setShowRescheduleConflictAlert(true);
      } else {
        setShowRescheduleConflictAlert(false);
        setRescheduleConflictingWorkouts([]);
      }
    } catch (error) {
      console.error('Error validating reschedule conflicts in real-time:', error);
      // Don't show alert on validation errors in real-time
    }
  };

  // Function to validate reschedule conflicts
  const validateRescheduleConflicts = async (rescheduleData) => {
    try {
      setIsValidatingRescheduleConflict(true);
      const response = await validateWorkoutConflict({
        date: rescheduleData.date,
        time: rescheduleData.time,
        end_time: rescheduleData.endTime,
        client_id: workout.client_id,
        exclude_workout_id: workout.id
      });

      if (response.data.hasConflicts) {
        setRescheduleConflictingWorkouts(response.data.conflicts);
        setShowRescheduleConflictAlert(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating reschedule conflicts:', error);
      // If validation fails, allow submission to proceed (fallback to server-side validation)
      return true;
    } finally {
      setIsValidatingRescheduleConflict(false);
    }
  };

  const handleRescheduleWorkout = async (e) => {
    e.preventDefault();
    
    // Prevent multiple clicks
    if (isRescheduling) {
      return;
    }
    
    if (!rescheduleForm.date || !rescheduleForm.time) {
      alert(t('workout.alert.fillDateTime'));
      return;
    }

    const normTime = (t) => (t || '').slice(0, 5);
    if (
      rescheduleForm.date === workout?.date &&
      normTime(rescheduleForm.time) === normTime(workout?.time) &&
      normTime(rescheduleForm.endTime) === normTime(workout?.end_time)
    ) {
      setShowRescheduleSameSlotAlert(true);
      return;
    }

    try {
      setIsRescheduling(true);
      
      // Validate conflicts before rescheduling
      const hasNoConflicts = await validateRescheduleConflicts({
        date: rescheduleForm.date,
        time: rescheduleForm.time,
        endTime: rescheduleForm.endTime
      });
      
      if (!hasNoConflicts) {
        setIsRescheduling(false);
        return; // Stop rescheduling if conflicts detected
      }

      // Close modal first to prevent UI freeze
      setShowRescheduleModal(false);

      // Используем новый endpoint для переноса
      const response = await transferWorkout(workout.id, {
        newDate: rescheduleForm.date,
        newTime: rescheduleForm.time,
        newEndTime: rescheduleForm.endTime || null
      });

      // Check if the transfer was successful - simplified check
      const isSuccess = response?.status === 200 || response?.statusText === 'OK' || response?.data?.newWorkout || response?.data?.originalWorkout;
      
      if (isSuccess) {
        // Notify parent to refresh
        if (onWorkoutUpdate) {
          onWorkoutUpdate();
        }
        // Close main modal
        onHide();
      } else {
        // Reopen modal if failed
        setShowRescheduleModal(true);
        throw new Error(t('workout.err.transferNoConfirm'));
      }
    } catch (error) {
      console.error('Error rescheduling workout:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });

      // Reopen modal on error
      setShowRescheduleModal(true);

      if (error.response) {
        const errorData = error.response.data;

        if (error.response.status === 409 && errorData.type === 'time_conflict') {
          alert(t('workout.alert.timeBusy', {
            clients: errorData.conflictingClients?.join(', ') || t('workout.alert.otherWorkouts'),
          }));
        } else if (error.response.status === 400 && errorData.type === 'same_client_conflict') {
          alert(t('workout.alert.sameClientConflict'));
        } else {
          alert(errorData.error || t('workout.err.transferHttp', { status: error.response.status }));
        }
      } else {
        alert(t('workout.err.transferNetwork'));
      }
    } finally {
      setIsRescheduling(false);
    }
  };

  const normTimeHm = (t) => {
    if (t == null || t === '') return '';
    const s = String(t);
    return s.length > 5 ? s.slice(0, 5) : s;
  };

  /** Длительность исходной тренировки в минутах (если конца нет — 60) */
  const getWorkoutDurationMinutes = () => {
    const start = normTimeHm(workout?.time);
    const end = normTimeHm(workout?.end_time);
    if (!start) return 60;
    if (!end) return 60;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    if ([sh, sm, eh, em].some((n) => !Number.isFinite(n))) return 60;
    let sMin = sh * 60 + sm;
    let eMin = eh * 60 + em;
    let d = eMin - sMin;
    if (d <= 0) d += 24 * 60;
    if (!Number.isFinite(d) || d <= 0) return 60;
    return d;
  };

  /** Конец слота при переносе: то же смещение от начала, что у исходной тренировки */
  const calculateRescheduleEndFromStart = (startTime) => {
    if (!startTime || typeof startTime !== 'string') {
      return '';
    }
    const duration = getWorkoutDurationMinutes();
    const [hours, minutes] = startTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return '';
    }
    let totalMin = hours * 60 + minutes + duration;
    totalMin = ((totalMin % (24 * 60)) + (24 * 60)) % (24 * 60);
    const endH = Math.floor(totalMin / 60);
    const endM = totalMin % 60;
    return `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;
  };

  const openRescheduleModal = () => {
    const t0 = normTimeHm(workout?.time);
    const et0 = normTimeHm(workout?.end_time);
    const defaultEndTime = t0 ? calculateRescheduleEndFromStart(t0) : '';

    setRescheduleForm({
      date: workout?.date || '',
      time: t0 || '',
      endTime: et0 || defaultEndTime
    });
    setShowRescheduleSameSlotAlert(false);
    setShowRescheduleConflictAlert(false);
    setShowRescheduleModal(true);
  };

  // Handle save action from header
  const handleSave = async () => {
    const commentDirty =
      (commentText || '').trim() !== (comment == null ? '' : String(comment)).trim();
    if (commentDirty) {
      await handleSaveComment();
    }
    // Close modal after save
    onHide();
  };

  // Несохранённый черновик комментария (в т.ч. когда поле открыто сразу при пустом комментарии)
  const hasUnsavedChanges =
    (commentText || '').trim() !== (comment == null ? '' : String(comment)).trim();

  /** Показываем textarea: при редактировании или если сохранённого комментария ещё нет */
  const showCommentEditor = isEditingComment || !comment;

  const renderExerciseForm = (containerProps = {}) => {
    const { className, ...rest } = containerProps || {};

    return (
      <div
        {...rest}
        className={cn("border rounded-lg p-4 bg-muted/30 space-y-4", className)}
      >
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">
            {editingExercise ? t('workout.form.editExercise') : t('workout.form.addExercise')}
          </Label>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowAddExercise(false);
              setEditingExercise(null);
              setExerciseForm({ name: '', sets: [] });
              setShowExerciseSelector(false);
              setSelectedExercise(null);
              setExerciseSearch('');
              setSelectedMuscleGroup(null);
              setShowPlanSelectorInForm(false);
              setSelectedPlan(null);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-green-800 font-medium">{successMessage}</p>
          </div>
        )}

        <form onSubmit={editingExercise ? handleUpdateExercise : handleAddExercise} className="space-y-4">
          <div className="space-y-2 relative">
            <Label htmlFor="exercise-name" className="text-sm font-medium">
              {t('workout.exerciseNameLabel')} <span className="text-destructive">*</span>
            </Label>
            <div ref={exerciseSelectorRef}>
              {!showExerciseSelector ? (
                <Input
                  id="exercise-name"
                  type="text"
                  readOnly
                  autoComplete="off"
                  value={exerciseNameForDisplay}
                  placeholder={t('workout.tapToPickExercise')}
                  className="cursor-pointer"
                  onClick={() => {
                    setExerciseSearch(exerciseNameForDisplay || exerciseForm.name || '');
                    setShowExerciseSelector(true);
                  }}
                />
              ) : (
                <div className="border rounded-lg bg-background shadow-sm flex flex-col">
                  {/* Поиск */}
                  <div className="p-2 border-b">
                    <Input
                      ref={exerciseSearchInputRef}
                      placeholder={t('workout.searchExercisesShort')}
                      value={exerciseSearch}
                      onChange={(e) => {
                        setExerciseSearch(e.target.value);
                        handleExerciseNameChange(e.target.value);
                      }}
                      className="w-full"
                      autoComplete="off"
                      enterKeyHint="search"
                    />
                  </div>

                  {/* Фильтры */}
                  <div className="p-2 border-b overflow-x-auto">
                    <div className="flex gap-1 min-w-max">
                      <Button
                        type="button"
                        variant={selectedMuscleGroup === null ? "default" : "outline"}
                        size="sm"
                        className="h-7 text-xs whitespace-nowrap"
                        onClick={() => setSelectedMuscleGroup(null)}
                      >
                        {t('plan.filterAll')}
                      </Button>
                      {muscleGroups.map((group) => (
                        <Button
                          type="button"
                          key={group.value}
                          variant={selectedMuscleGroup === group.value ? "default" : "outline"}
                          size="sm"
                          className={`h-7 text-xs whitespace-nowrap ${selectedMuscleGroup === group.value ? group.color : ''}`}
                          onClick={() => setSelectedMuscleGroup(
                            selectedMuscleGroup === group.value ? null : group.value
                          )}
                        >
                          {group.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Список */}
                  <div className="overflow-y-auto max-h-[240px]">
                    {filteredExercises.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground space-y-3">
                        <div>{t('plan.noExercisesFound')}</div>
                        {exerciseSearch?.trim()?.length > 1 && (
                          <div className="flex justify-center">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                const name = exerciseSearch.trim();
                                setPendingExerciseName(name);
                                setPendingMuscleGroup(selectedMuscleGroup || 'other');
                                setPendingExerciseAction(null);
                                setShowExerciseConfirmDialog(true);
                              }}
                            >
                              {t('workout.addToDictButton', { name: exerciseSearch.trim() })}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-1">
                        {filteredExercises.map((exercise) => {
                          const exerciseName = exercise.label || exercise.value || exercise.name || '';
                          const exerciseId = exercise.value || exercise.id || exerciseName;
                          const isSelected =
                            selectedExercise &&
                            typeof selectedExercise !== 'string' &&
                            (selectedExercise.value || selectedExercise.name) === exercise.value;
                          const muscleGroup = exercise.muscle_group;
                          const muscleGroupBadge = muscleGroup
                            ? muscleGroups.find(g => g.value === muscleGroup)
                            : null;

                          return (
                            <div
                              key={exerciseId}
                              onClick={() => handleSelectExercise(exercise)}
                              className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-accent transition-colors"
                            >
                              <Dumbbell className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <span className="flex-1 truncate text-sm">{exerciseName}</span>
                              {muscleGroupBadge && (
                                <Badge
                                  variant="outline"
                                  className={`${muscleGroupBadge.color} text-xs flex-shrink-0`}
                                >
                                  {muscleGroupBadge.label}
                                </Badge>
                              )}
                              {isSelected && <Check className="h-4 w-4 flex-shrink-0 ml-auto text-primary" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Exercise analytics (last session + max weight) */}
            {showAddExercise && exerciseHasHistory && (
              <div className="pt-2">
                {Boolean(workout?.isGroup) || (Array.isArray(workout?.allClients) && workout.allClients.length > 1) ? (
                  <div className="text-xs text-muted-foreground">
                    {t('workout.historyGroupOnly')}
                  </div>
                ) : !workout?.client_id ? (
                  <div className="text-xs text-muted-foreground">
                    {t('workout.historyNoClient')}
                  </div>
                ) : !exerciseForm?.name?.trim() ? null : (
                  <Card className="bg-background">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">{t('workout.exerciseHistoryTitle')}</div>
                        {exerciseHistoryLoading && (
                          <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
                        )}
                      </div>

                      {exerciseHistoryError && (
                        <div className="text-xs text-destructive mt-1">{exerciseHistoryError}</div>
                      )}

                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">{t('workout.lastSession')}</div>
                          {exerciseLastSession ? (
                            <div className="mt-1">
                              <div className="font-medium">
                                {exerciseLastSession.date}{exerciseLastSession.time ? `, ${exerciseLastSession.time}` : ''}
                              </div>
                              {Array.isArray(exerciseLastSession.sets) && exerciseLastSession.sets.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {exerciseLastSession.sets.map((s, idx) => (
                                    <div key={s.id || idx} className="text-xs text-muted-foreground">
                                      #{s.set_number || (idx + 1)}: {s.reps || 0}
                                      {typeof s.weight === 'number' && isFinite(s.weight) ? t('workout.setWeightPart', { w: s.weight }) : ''}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mt-1 text-xs text-muted-foreground">{t('workout.noHistoryData')}</div>
                          )}
                        </div>

                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">{t('workout.workingWeight')}</div>
                          <div className="mt-1 font-medium">
                            {(() => {
                              const ww = workingWeightsByName?.[exerciseForm?.name?.trim()]?.working_weight;
                              const fallback = exerciseMaxWeight;
                              const val =
                                typeof ww === 'number' && isFinite(ww)
                                  ? ww
                                  : (typeof fallback === 'number' && isFinite(fallback) ? fallback : null);
                              return val !== null ? `${val} ${t('plan.kg')}` : '—';
                            })()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Duplicate Exercise Alert */}
            {showDuplicateAlert && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('workout.duplicateExerciseTitle')}</AlertTitle>
                <AlertDescription>
                  {t('workout.duplicateExerciseDesc', { name: duplicateExerciseName })}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label>{t('workout.setsLabel')}</Label>
              <span className="text-sm text-muted-foreground">
                ({exerciseForm.sets.length})
              </span>
            </div>

            {exerciseForm.sets.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-3 sm:p-4">
                  <div className="text-muted-foreground mb-4 text-center">
                    <p className="text-sm font-medium mb-2">{t('workout.noSetsTitle')}</p>
                    <p className="text-xs">{t('workout.noSetsHint')}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddSet}
                    className="w-full sm:w-auto"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('workout.addFirstSet')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              exerciseForm.sets.map((set, index) => (
                <Card key={index}>
                  <CardContent className="p-2 sm:p-3">
                    <div className="flex flex-row items-center space-x-2 sm:space-x-4 overflow-x-auto w-full">
                      {/* Set Number */}
                      <div className="flex items-center space-x-2 flex-shrink-0 min-w-0">
                        <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 bg-primary/10 rounded-full flex-shrink-0">
                          <Hash className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary" />
                        </div>
                        <div className="flex items-center flex-shrink-0">
                          <span className="font-semibold text-xs sm:text-sm min-w-[16px] sm:min-w-[20px]">#{index + 1}</span>
                        </div>
                      </div>

                      {/* Reps Input */}
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div
                          className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 bg-blue-50 rounded-full cursor-pointer hover:bg-blue-100 transition-colors flex-shrink-0"
                          onClick={() => focusInput(index, 'reps')}
                          title={t('workout.focusRepsTitle')}
                        >
                          <Repeat2 className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-600" />
                        </div>
                        <div className="flex items-center flex-1 min-w-0">
                          <Input
                            type="number"
                            placeholder={t('plan.repsPlaceholder')}
                            value={set.reps}
                            onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                            className="w-full text-center"
                            required
                            min="1"
                            step="1"
                          />
                        </div>
                      </div>

                      {/* Weight Input */}
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div
                          className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 bg-green-50 rounded-full cursor-pointer hover:bg-green-100 transition-colors flex-shrink-0"
                          onClick={() => focusInput(index, 'weight')}
                          title={t('workout.focusWeightTitle')}
                        >
                          <Dumbbell className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600" />
                        </div>
                        <div className="flex items-center space-x-1 flex-1 min-w-0">
                          <Input
                            type="number"
                            placeholder={t('workout.weightInputShort')}
                            value={set.weight}
                            onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                            className="w-full text-center"
                            min="0"
                            step="any"
                          />
                          <span className="text-xs text-muted-foreground font-medium flex-shrink-0">{t('plan.kg')}</span>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <div className="flex items-center flex-shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveSet(index)}
                          className="text-red-600 border-red-600 hover:bg-red-50 hover:border-red-700 h-6 w-6 sm:h-7 sm:w-7 p-0 flex-shrink-0"
                        >
                          <Trash2 className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {/* Button to add more sets */}
            {exerciseForm.sets.length > 0 && exerciseForm.sets.length < 10 && (
              <div className="flex justify-center mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddSet}
                  className="w-full"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('workout.addAnotherSet')}
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddExercise(false);
                setEditingExercise(null);
                setExerciseForm({ name: '', sets: [] });
                setShowExerciseSelector(false);
                setSelectedExercise(null);
                setExerciseSearch('');
                setSelectedMuscleGroup(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isAddingExercise}
              className="touch-manipulation"
            >
              {isAddingExercise ? t('common.saving') : (editingExercise ? t('common.save') : t('common.add'))}
            </Button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <>
      <FullScreenModal open={show} onOpenChange={onHide}>
        <FullScreenModalContent className="flex flex-col">
          {/* Header with Navigation */}
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={onHide} />
            <FullScreenModalTitle>
              {workout?.status === 'transferred' ? (
                <div className="flex items-center justify-center gap-2">
                  <span>{t('workout.detail.meetingDetails')}</span>
                  <Badge variant="destructive" className="text-xs">{t('workout.detail.badgeTransferred')}</Badge>
                </div>
              ) : workout?.status === 'cancelled' ? (
                t('workout.detail.meetingDetails')
              ) : workout?.status === 'skipped' ? (
                <div className="flex items-center justify-center gap-2">
                  <span>{t('workout.detail.meetingDetails')}</span>
                  <Badge variant="destructive" className="text-xs">{t('workout.detail.badgeSkipped')}</Badge>
                </div>
              ) : (
                t('workout.detail.editingTitle')
              )}
            </FullScreenModalTitle>
            <div className="w-11 h-11 shrink-0" aria-hidden />
          </FullScreenModalHeader>

          {/* Body Content */}
          <FullScreenModalBody>
            {/* Workout Info - Mobile Compact Layout */}
            <div className="mb-6">
            {/* Mobile: Horizontal layout for Client, Date/Time, and Location */}
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 sm:gap-4 mb-4">
              {/* Client Name - Mobile compact */}
              <div className="flex-1 min-w-0">
                <Card className="h-full">
                  <CardContent className="p-2 sm:p-4">
                    <div
                      className="flex items-center space-x-2 cursor-pointer hover:bg-accent/50 rounded-md p-1 sm:p-2 -m-1 sm:-m-2 transition-colors"
                      onClick={openClientPickerOrProfile}
                      title={isGroupWorkout ? t('workout.title.pickClient') : t('workout.title.openClientProfile')}
                    >
                      <User className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm sm:text-base font-medium">{t('common.client')}</p>
                        <p className="text-sm sm:text-base text-muted-foreground truncate">
                          {clientName || t('workout.location.notSetOption')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Date/Time - Mobile compact */}
              <div className="flex-1 min-w-0">
                <Card className="h-full">
                  <CardContent className="p-2 sm:p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm sm:text-base font-medium">{t('workout.field.dateTime')}</p>
                          <p className="text-sm sm:text-base text-muted-foreground truncate">
                            {formatWorkoutDateTime(workout?.date, workout?.time, workout?.end_time)}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={openRescheduleModal}
                        variant="ghost"
                        size="sm"
                        className="h-10 w-10 p-0 hover:bg-accent touch-manipulation flex-shrink-0"
                        title={t('workout.editDateTimeTitle')}
                        style={{ minWidth: '44px', minHeight: '44px' }}
                      >
                        <Edit className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Location - Mobile compact */}
              <div className="flex-1 min-w-0">
                <Card className="h-full">
                  <CardContent className="p-2 sm:p-4">
                    {isEditingLocation ? (
                      <div className="space-y-2">
                        {showAddLocationForm ? (
                          <div className="space-y-2">
                            <div className="space-y-1">
                              <Label htmlFor="wd-new-location-name" className="text-xs">{t('workout.location.gymNameLabel')}</Label>
                              <Input
                                id="wd-new-location-name"
                                value={newLocationName}
                                onChange={(e) => setNewLocationName(e.target.value)}
                                placeholder={t('workout.location.gymNamePh')}
                                className="h-9 placeholder:text-muted-foreground"
                                autoFocus
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="wd-new-location-address" className="text-xs">{t('workout.location.gymAddressLabel')}</Label>
                              <Input
                                id="wd-new-location-address"
                                value={newLocationAddress}
                                onChange={(e) => setNewLocationAddress(e.target.value)}
                                placeholder={t('workout.location.gymAddressPh')}
                                className="h-9 placeholder:text-muted-foreground"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleAddLocation}
                                size="sm"
                                disabled={!newLocationName.trim() || isAddingLocation}
                                className="h-8 px-3 text-sm flex-1"
                              >
                                {isAddingLocation ? t('common.saving') : t('common.save')}
                              </Button>
                              <Button
                                onClick={() => {
                                  setShowAddLocationForm(false);
                                  setNewLocationName('');
                                  setNewLocationAddress('');
                                }}
                                variant="outline"
                                size="sm"
                                disabled={isAddingLocation}
                                className="h-8 px-3 text-sm"
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <select
                              value={locationValue || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === 'add_new') {
                                  setShowAddLocationForm(true);
                                  return;
                                }
                                if (value === 'online') {
                                  setLocationValue('online');
                                  setLocationType('online');
                                } else if (value === '') {
                                  setLocationValue('');
                                  setLocationType(null);
                                } else {
                                  setLocationValue(value);
                                  setLocationType('offline');
                                }
                              }}
                              className="flex h-11 md:h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              autoFocus
                            >
                              <option value="">{t('workout.location.notSetOption')}</option>
                              <option value="online">{t('trainer.online')}</option>
                              {trainerLocations.map((location) => (
                                <option key={location.id} value={location.id}>
                                  {location.name}
                                  {location.address ? ` — ${location.address}` : ''}
                                </option>
                              ))}
                              <option value="add_new">{t('workout.location.addNewGym')}</option>
                            </select>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleSaveLocation}
                                size="sm"
                                disabled={isUpdatingLocation}
                                className="h-6 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm flex-1"
                              >
                                {isUpdatingLocation ? t('common.saving') : t('common.save')}
                              </Button>
                              <Button
                                onClick={() => {
                                  setIsEditingLocation(false);
                                  setShowAddLocationForm(false);
                                  setNewLocationName('');
                                  setNewLocationAddress('');
                                  const w = workout;
                                  const isOnlineLoc =
                                    w?.location === 'online' ||
                                    w?.location === API_LOCATION_ONLINE_LABEL ||
                                    w?.location_type === 'online';
                                  const workoutLocationType =
                                    w?.location_type ||
                                    (isOnlineLoc ? 'online' : w?.location_id ? 'offline' : null);
                                  if (w?.location_id) {
                                    setLocationValue(w.location_id);
                                  } else if (workoutLocationType === 'online') {
                                    setLocationValue('online');
                                  } else {
                                    setLocationValue('');
                                  }
                                  setLocationType(workoutLocationType);
                                }}
                                variant="outline"
                                size="sm"
                                disabled={isUpdatingLocation || isAddingLocation}
                                className="h-6 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm sm:text-base font-medium">{t('workout.location.sectionTitle')}</p>
                            <p className="text-sm sm:text-base text-muted-foreground truncate">
                              {locationType === 'online'
                                ? t('workout.location.pinOnline')
                                : locationType === 'offline' && locationValue
                                  ? trainerLocations.find((l) => l.id === locationValue)
                                    ? `📍 ${trainerLocations.find((l) => l.id === locationValue).name}${
                                        trainerLocations.find((l) => l.id === locationValue).address
                                          ? `, ${trainerLocations.find((l) => l.id === locationValue).address}`
                                          : ''
                                      }`
                                    : workout?.location || t('workout.location.notSetOption')
                                  : workout?.location === API_LOCATION_ONLINE_LABEL || workout?.location === 'online'
                                    ? t('workout.location.pinOnline')
                                    : workout?.location
                                      ? `📍 ${workout.location}`
                                      : t('workout.location.notSetOption')}
                            </p>
                          </div>
                        </div>
                        {(locationType || locationValue || workout?.location) && (
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => setIsEditingLocation(true)}
                              variant="ghost"
                              size="sm"
                              className="h-10 w-10 p-0 hover:bg-accent touch-manipulation"
                              title={t('workout.editLocationTitle')}
                              style={{ minWidth: '44px', minHeight: '44px' }}
                            >
                              <Edit className="h-5 w-5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-10 w-10 p-0 text-destructive hover:bg-destructive/10 touch-manipulation"
                                  title={t('workout.removeLocationTitle')}
                                  style={{ minWidth: '44px', minHeight: '44px' }}
                                >
                                  <Trash2 className="h-5 w-5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{t('workout.removeLocationTitle')}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t('workout.removeLocationDesc')}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleDeleteLocation}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    {t('common.delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                        {!locationType && !locationValue && !workout?.location && (
                          <Button
                            onClick={() => setIsEditingLocation(true)}
                            variant="ghost"
                            size="sm"
                            className="h-10 w-10 p-0 hover:bg-accent touch-manipulation"
                            title={t('workout.addLocationTitle')}
                            style={{ minWidth: '44px', minHeight: '44px' }}
                          >
                            <Edit className="h-5 w-5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Comment Section - Mobile Compact */}
            <div className="mb-6">
              <Card>
                <CardContent className="p-2 sm:p-4">
                  <div className="space-y-2 sm:space-y-3">
                    {/* Label with Edit/Delete Icons - Mobile Compact */}
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-medium">
                        <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{t('workout.comment.fullLabel')}</span>
                        <span className="sm:hidden">{t('workout.comment.shortLabel')}</span>
                      </Label>
                      
                      {/* Edit/Delete Icons - Positioned above comment text */}
                      {comment && !showCommentEditor && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={handleEditComment}
                            variant="ghost"
                            size="sm"
                            className="h-10 w-10 p-0 hover:bg-accent touch-manipulation"
                            title={t('workout.editCommentTitle')}
                            style={{ minWidth: '44px', minHeight: '44px' }}
                          >
                            <Edit className="h-5 w-5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 text-destructive hover:bg-destructive/10 touch-manipulation"
                                title={t('workout.deleteCommentTitle')}
                                style={{ minWidth: '44px', minHeight: '44px' }}
                              >
                                <Trash2 className="h-5 w-5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('workout.deleteCommentTitle')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('workout.deleteCommentDesc')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={handleDeleteComment}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>

                    {showCommentEditor ? (
                      <div className="space-y-2">
                        <Textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder={t('workout.commentPlaceholder')}
                          className="resize-none"
                          rows={2}
                        />
                        <div className="flex gap-1 sm:gap-2">
                          <Button
                            onClick={handleSaveComment}
                            size="sm"
                            disabled={isUpdatingComment}
                            className="h-6 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                          >
                            {isUpdatingComment ? t('common.saving') : t('common.save')}
                          </Button>
                          <Button
                            onClick={handleCancelEditComment}
                            variant="outline"
                            size="sm"
                            disabled={isUpdatingComment}
                            className="h-6 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Compact comment display */}
                        <div className="text-xs sm:text-sm text-foreground bg-muted px-2 sm:px-3 py-1 sm:py-2 rounded-md min-h-[40px] sm:min-h-[60px] border">
                          {comment}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>

          {/* Previous Workout Section */}
          {!isGroupWorkout && previousWorkout && (
            <div className="mb-6">
              <Card className="border border-primary/20 shadow-sm">
                <CardContent className="p-0">
                  <div className="w-full flex items-center justify-between p-4">
                    <button
                      onClick={() => setShowPreviousWorkout(!showPreviousWorkout)}
                      className="flex-1 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg p-2 -m-2"
                      type="button"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                          <Clock className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-sm sm:text-base font-semibold">{t('workout.previousWorkout')}</h4>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {formatWorkoutDateTime(previousWorkout.date, previousWorkout.time, previousWorkout.end_time)}
                          </p>
                        </div>
                      </div>
                      {showPreviousWorkout ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>
                    
                    {/* Navigation arrows */}
                    {completedWorkouts.length > 1 && (
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToPreviousCompletedWorkout();
                          }}
                          disabled={currentCompletedWorkoutIndex >= completedWorkouts.length - 1}
                          className={`p-1.5 rounded-md transition-colors ${
                            currentCompletedWorkoutIndex >= completedWorkouts.length - 1
                              ? 'text-muted-foreground/30 cursor-not-allowed'
                              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                          }`}
                          type="button"
                          title={t('workout.prevWorkoutNavTitle')}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToNextCompletedWorkout();
                          }}
                          disabled={currentCompletedWorkoutIndex === 0}
                          className={`p-1.5 rounded-md transition-colors ${
                            currentCompletedWorkoutIndex === 0
                              ? 'text-muted-foreground/30 cursor-not-allowed'
                              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                          }`}
                          type="button"
                          title={t('workout.nextWorkoutNavTitle')}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {showPreviousWorkout && (() => {
                    const prevComment = getPreviousWorkoutCommentText(previousWorkout);
                    return (
                    <div className="border-t bg-muted/20 px-4 pb-4 pt-4 space-y-3">
                      {prevComment ? (
                        <div className="rounded-md border bg-background px-3 py-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">{t('plan.comment')}</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                            {prevComment}
                          </p>
                        </div>
                      ) : null}
                      {loadingPreviousWorkout ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      ) : previousWorkoutExercises.length > 0 ? (
                        <div className="space-y-2 sm:space-y-3">
                          {previousWorkoutExercises.map((exercise) => (
                            <Card key={exercise.id} className="bg-background border shadow-sm">
                              <CardContent className="p-3 sm:p-4">
                                <div className="mb-2">
                                  <h5 className="font-semibold text-sm sm:text-base">{exercise.name}</h5>
                                </div>
                                <div className="space-y-1">
                                  {exercise.sets && exercise.sets.map((set, index) => (
                                    <div key={set.id || index} className="text-xs sm:text-sm text-muted-foreground">
                                      {t('workout.setLine', { n: index + 1, reps: set.reps })}
                                      {set.weight ? t('workout.setWeightPart', { w: set.weight }) : ''}
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : !prevComment ? (
                        <div className="text-center py-8">
                          <div className="flex flex-col items-center gap-2">
                            <Dumbbell className="h-8 w-8 text-muted-foreground/50" />
                            <p className="text-sm text-muted-foreground">{t('workout.noPrevWorkoutData')}</p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Exercises Section - Mobile Compact */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <div className="flex-1">
                <h4 className="text-sm sm:text-lg font-semibold">{t('workout.exercisesHeading')}</h4>
                {workoutPlanId && (
                  <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                    {t('workout.planLabel')} {trainingPlans.find(p => p.id === workoutPlanId)?.name || t('workout.unknownPlan')}
                  </div>
                )}
              </div>
              {hasExercises && (
                <div className="flex items-center gap-1 sm:gap-2">
                  {workoutPlanId ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPlanAssignment(true)}
                        className="text-xs sm:text-sm h-7 sm:h-8 px-2 sm:px-3"
                      >
                        <Dumbbell className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{t('workout.replacePlan')}</span>
                        <span className="sm:hidden">{t('workout.replaceShort')}</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddExercise(!showAddExercise);
                          if (!showAddExercise) {
                            setShowExerciseSelector(false);
                            setSelectedExercise(null);
                            setExerciseForm({ name: '', sets: [] });
                          }
                        }}
                        className="text-xs sm:text-sm h-7 sm:h-8 px-2 sm:px-3"
                      >
                        <Plus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{t('workout.addExerciseFull')}</span>
                        <span className="sm:hidden">{t('common.add')}</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddExercise(!showAddExercise);
                          if (!showAddExercise) {
                            setShowExerciseSelector(false);
                            setSelectedExercise(null);
                            setExerciseForm({ name: '', sets: [] });
                          }
                          // Закрываем селектор планов если открыт
                          if (showPlanSelector) {
                            setShowPlanSelector(false);
                          }
                        }}
                        className="text-xs sm:text-sm h-7 sm:h-8 px-2 sm:px-3"
                      >
                        <Plus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{t('workout.addExerciseFull')}</span>
                        <span className="sm:hidden">{t('common.add')}</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowPlanSelector(!showPlanSelector);
                          // Закрываем форму добавления упражнения если открыта
                          if (showAddExercise) {
                            setShowAddExercise(false);
                            setShowExerciseSelector(false);
                            setSelectedExercise(null);
                            setExerciseForm({ name: '', sets: [] });
                          }
                        }}
                        className="text-xs sm:text-sm h-7 sm:h-8 px-2 sm:px-3"
                        disabled={trainingPlans.length === 0}
                      >
                        <Dumbbell className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{t('workout.addPlan')}</span>
                        <span className="sm:hidden">{t('workout.planShort')}</span>
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {!hasExercises && (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto justify-center text-base py-3"
                  onClick={() => {
                    setShowAddExercise(true);
                    setShowExerciseSelector(false);
                    setSelectedExercise(null);
                    setExerciseForm({ name: '', sets: [] });
                    // закрываем селектор планов
                    if (showPlanSelector) setShowPlanSelector(false);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('workout.addExerciseFull')}
                </Button>
                {showAddExercise && !editingExercise && (
                  <div className="w-full sm:order-4">
                    {renderExerciseForm({ className: "mt-4 sm:mt-0" })}
                  </div>
                )}
                <div className="text-center text-sm text-muted-foreground sm:w-auto">{t('workout.or')}</div>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto justify-center text-base py-3"
                  disabled={trainingPlans.length === 0}
                  onClick={() => {
                    setShowPlanSelector(!showPlanSelector);
                    // Закрываем форму добавления упражнения если открыта
                    if (showAddExercise) {
                      setShowAddExercise(false);
                      setShowExerciseSelector(false);
                      setSelectedExercise(null);
                      setExerciseForm({ name: '', sets: [] });
                    }
                  }}
                >
                  <Dumbbell className="mr-2 h-4 w-4" />
                  {t('workout.addReadyMadePlan')}
                </Button>
              </div>
            )}

            {/* Training Plans Selector */}
            {showPlanSelector && (
              <div className="mb-4 border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">{t('workout.pickTrainingPlan')}</Label>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowPlanSelector(false);
                      setSelectedPlan(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {trainingPlans.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">{t('workout.noPlansAvailable')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {trainingPlans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => handleAddPlanExercisesToExisting(plan)}
                        className="p-3 border rounded-md cursor-pointer hover:bg-accent transition-colors active:bg-accent/80"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium">{plan.name}</div>
                            {plan.description && (
                              <div className="text-sm text-muted-foreground mt-1">{plan.description}</div>
                            )}
                            <div className="text-xs text-muted-foreground mt-1">
                              {Array.isArray(plan.exercises) && plan.exercises.length > 0
                                ? plan.exercises
                                    .map(ex => ex.exercise_name || ex.name)
                                    .filter(Boolean)
                                    .join(', ')
                                : t('workout.noExercisesInPlanCard')}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddPlanExercisesToExisting(plan);
                            }}
                            className="ml-2 flex-shrink-0"
                          >
                            {t('common.add')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Inline Add Exercise Form (when there are already exercises) */}
            {hasExercises && showAddExercise && !editingExercise && renderExerciseForm({ className: "mb-4" })}

            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : exercises.length === 0 ? (
              <p className="text-muted-foreground">{t('workout.noExercises')}</p>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {exercises.map((exercise) => (
                  <React.Fragment key={exercise.id}>
                    <Card>
                    <CardContent className="p-2 sm:p-4">
                      <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                            <h5 className="font-semibold text-sm sm:text-base truncate">{exercise.name}</h5>
                            <div className="flex space-x-1 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditExercise(exercise)}
                                className="h-11 w-11 p-0 touch-manipulation"
                                title={t('workout.editExerciseTitle')}
                                style={{ minWidth: '44px', minHeight: '44px' }}
                              >
                                <Edit className="h-5 w-5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 border-red-600 hover:bg-red-50 h-11 w-11 p-0 touch-manipulation"
                                    onClick={() => setExerciseToDelete(exercise.id)}
                                    title={t('workout.deleteExerciseTitle')}
                                    style={{ minWidth: '44px', minHeight: '44px' }}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>{t('workout.deleteExerciseConfirmTitle')}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {t('workout.deleteExerciseConfirmDesc')}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel onClick={cancelDeleteExercise}>{t('common.cancel')}</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={confirmDeleteExercise}
                                      className="bg-red-600 text-white hover:bg-red-700"
                                    >
                                      {t('common.continue')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>

                            {/* Working weight */}
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="text-xs sm:text-sm text-muted-foreground">
                                {t('workout.workingWeightColon')}{' '}
                                <span className="font-medium text-foreground">
                                  {(() => {
                                    const row = workingWeightsByName?.[exercise.name];
                                    const ww = row?.working_weight;
                                    return (typeof ww === 'number' && isFinite(ww)) ? `${ww} ${t('plan.kg')}` : '—';
                                  })()}
                                </span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-9 px-3 touch-manipulation"
                                onClick={() => {
                                  const isGroup = Boolean(workout?.isGroup) || (Array.isArray(workout?.allClients) && workout.allClients.length > 1);
                                  if (isGroup || !workout?.client_id) {
                                    alert(t('workout.workingWeightGroupAlert'));
                                    return;
                                  }
                                  setWorkingWeightEditExerciseId(exercise.id);
                                  const ww = workingWeightsByName?.[exercise.name]?.manual_working_weight;
                                  setWorkingWeightInput(
                                    typeof ww === 'number' && isFinite(ww) ? String(ww) : ''
                                  );
                                }}
                                title={t('workout.updateWorkingWeightTitle')}
                              >
                                {t('workout.updateWeightBtn')}
                              </Button>
                            </div>

                            {workingWeightEditExerciseId === exercise.id && (
                              <div className="border rounded-md p-3 bg-background mb-2">
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <Label className="text-xs text-muted-foreground">{t('workout.workingWeightKgLabel')}</Label>
                                    <Input
                                      type="number"
                                      value={workingWeightInput}
                                      onChange={(e) => setWorkingWeightInput(e.target.value)}
                                      placeholder={t('workout.weightExamplePh')}
                                      min="0"
                                      step="any"
                                    />
                                  </div>
                                  <Button
                                    className="touch-manipulation"
                                    disabled={isSavingWorkingWeight}
                                    onClick={async () => {
                                      setIsSavingWorkingWeight(true);
                                      try {
                                        const val = workingWeightInput.trim();
                                        const clientId = workout?.client_id;
                                        if (!clientId) throw new Error(t('workout.err.noWorkoutClient'));
                                        const payloadVal = val === '' ? null : val;
                                        await updateClientExerciseWorkingWeight(clientId, exercise.name, payloadVal);

                                        // Refresh working weights map for this workout
                                        const names = (Array.isArray(exercises) ? exercises : [])
                                          .map(e => (e?.name || '').trim())
                                          .filter(Boolean);
                                        const unique = Array.from(new Set(names));
                                        const resp = await getClientExerciseWorkingWeights(clientId, unique);
                                        const rows = Array.isArray(resp?.data) ? resp.data : [];
                                        const map = {};
                                        for (const r of rows) {
                                          if (r && r.exercise_name) map[r.exercise_name] = r;
                                        }
                                        setWorkingWeightsByName(map);

                                        setWorkingWeightEditExerciseId(null);
                                      } catch (e) {
                                        console.error('[WorkoutDetailModal] Failed to update working weight:', e);
                                        const msg =
                                          e?.response?.data?.error ||
                                          e?.response?.data?.message ||
                                          e?.message ||
                                          t('workout.err.workingWeightUpdate');
                                        alert(msg);
                                      } finally {
                                        setIsSavingWorkingWeight(false);
                                      }
                                    }}
                                  >
                                    {isSavingWorkingWeight ? t('common.saving') : t('common.save')}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="touch-manipulation"
                                    disabled={isSavingWorkingWeight}
                                    onClick={() => setWorkingWeightEditExerciseId(null)}
                                  >
                                    {t('common.cancel')}
                                  </Button>
                                </div>
                              </div>
                            )}

                          <div className="space-y-1">
                            {exercise.sets && exercise.sets.map((set, index) => (
                              <div key={set.id || index} className="text-xs sm:text-sm text-muted-foreground">
                                {t('workout.setLine', { n: index + 1, reps: set.reps })}
                                {set.weight ? t('workout.setWeightPart', { w: set.weight }) : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                    {/* Inline edit form under the exercise being edited */}
                    {showAddExercise && editingExercise && editingExercise.id === exercise.id && (
                      <>
                        <div className="h-2" />
                        {renderExerciseForm({ ref: inlineEditFormRef, className: "mt-0" })}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
           </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 mt-6 pt-4 border-t">
              {workout?.status === 'cancelled' ? (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleRestoreWorkout}
                  className="text-green-600 border-green-600 hover:bg-green-50 h-12"
                >
                  {t('workout.restoreSession')}
                </Button>
              ) : workout?.status === 'transferred' ? (
                <Button
                  variant="outline"
                  size="lg"
                  disabled
                  className="h-12"
                >
                  {t('workout.movedSession')}
                </Button>
              ) : workout?.status === 'skipped' ? (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleRestoreWorkout}
                  className="text-green-600 border-green-600 hover:bg-green-50 h-12"
                >
                  {t('workout.restoreSession')}
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    onClick={handleSave}
                    disabled={isUpdatingComment}
                    className="h-12 w-full touch-manipulation"
                  >
                    {isUpdatingComment ? t('common.saving') : t('common.save')}
                  </Button>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleCancelWorkout}
                      className="h-12 w-full touch-manipulation bg-background text-red-600 border border-red-500/80 hover:bg-red-50 hover:text-red-700 hover:border-red-600"
                    >
                      {t('workout.cancelSession')}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleSkipWorkout}
                      className="h-12 w-full touch-manipulation bg-background text-red-600 border border-red-500/80 hover:bg-red-50 hover:text-red-700 hover:border-red-600"
                    >
                      {t('workout.markSkipBtn')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>


      {/* Reschedule Workout Panel */}
      <FullScreenModal open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowRescheduleModal(false)} />
            <FullScreenModalTitle>{t('workout.rescheduleTitle')}</FullScreenModalTitle>
            <Button
              type="submit"
              form="reschedule-workout-form"
              variant="ghost"
              disabled={isValidatingRescheduleConflict || showRescheduleConflictAlert || showRescheduleSameSlotAlert || isRescheduling}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {isRescheduling ? t('workout.rescheduleMoving') : isValidatingRescheduleConflict ? t('workout.rescheduleValidating') : t('workout.rescheduleSubmit')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
          <form id="reschedule-workout-form" onSubmit={handleRescheduleWorkout} className="space-y-4 pb-6">
            <div className="text-sm text-muted-foreground">
              {t('workout.rescheduleHint')}
            </div>
            {showRescheduleConflictAlert && (
              <Alert variant="destructive" className="border-red-500 bg-white">
                <Terminal className="h-6 w-6" />
                <AlertTitle className="text-sm font-medium">{t('workout.conflictTitle')}</AlertTitle>
                <AlertDescription className="text-xs">
                  <div className="space-y-3">
                    <p>{t('workout.conflictTimeTaken')}</p>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <h4 className="font-medium text-red-800 mb-2 text-xs">{t('workout.conflictingListTitle')}</h4>
                      <div className="space-y-2">
                        {rescheduleConflictingWorkouts.length > 0 ? (
                          rescheduleConflictingWorkouts.map((workout, index) => (
                            <div key={index} className="text-xs text-red-700 bg-white rounded p-2 border border-red-200">
                              <div className="font-medium">
                                {displayClientName(workout.client_name || workout.client_nickname) || t('workout.clientNotSpecified')}
                              </div>
                              <div className="text-gray-600">
                                {formatDate(workout.date)}, {formatTime(workout.time)}
                                {workout.end_time && ` - ${formatTime(workout.end_time)}`}
                              </div>
                              {workout.comment && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {workout.comment}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-red-700">
                            {t('workout.conflictOverlapFallback')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowRescheduleConflictAlert(false)}
                        className="bg-green-600 hover:bg-green-700 text-white w-24 h-10"
                      >
                        OK
                      </Button>
                    </div>

                  </div>
                </AlertDescription>
              </Alert>
            )}

            {showRescheduleSameSlotAlert && (
              <Alert className="border-amber-500 bg-amber-50">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <AlertTitle className="text-sm font-medium text-amber-800">{t('workout.sameSlotTitle')}</AlertTitle>
                <AlertDescription className="text-xs text-amber-700">
                  {t('workout.sameSlotDesc')}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="reschedule-date">{t('workout.newDateLabel')}</Label>
              <Input
                id="reschedule-date"
                type="date"
                value={rescheduleForm.date}
                onChange={(e) => {
                  setRescheduleForm({...rescheduleForm, date: e.target.value});
                  setShowRescheduleSameSlotAlert(false);
                  if (rescheduleForm.time && rescheduleForm.endTime) {
                    validateRescheduleConflictsRealtime({
                      ...rescheduleForm,
                      date: e.target.value
                    });
                  }
                }}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="reschedule-time">{t('workout.startTimeLabel')}</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  value={rescheduleForm.time}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    const newEndTime = calculateRescheduleEndFromStart(newTime);
                    const updatedForm = {
                      ...rescheduleForm, 
                      time: newTime,
                      endTime: newEndTime
                    };
                    setRescheduleForm(updatedForm);
                    setShowRescheduleSameSlotAlert(false);
                    if (rescheduleForm.date && newEndTime) {
                      validateRescheduleConflictsRealtime({
                        ...updatedForm,
                        time: newTime,
                        endTime: newEndTime
                      });
                    }
                  }}
                  required
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="reschedule-end-time">{t('workout.endTimeLabel')}</Label>
                <Input
                  id="reschedule-end-time"
                  type="time"
                  value={rescheduleForm.endTime}
                  onChange={(e) => {
                    setRescheduleForm({...rescheduleForm, endTime: e.target.value});
                    setShowRescheduleSameSlotAlert(false);
                    if (rescheduleForm.date && rescheduleForm.time) {
                      validateRescheduleConflictsRealtime({
                        ...rescheduleForm,
                        endTime: e.target.value
                      });
                    }
                  }}
                  placeholder={t('workout.endTimePh')}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {t('workout.durationHint')}
            </p>
          </form>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Exercise Confirmation Dialog */}
      <ExerciseConfirmDialog
        isOpen={showExerciseConfirmDialog}
        onClose={() => setShowExerciseConfirmDialog(false)}
        onConfirm={confirmSaveExercise}
        title={t('workout.exerciseConfirmTitle')}
        description={t('workout.exerciseConfirmDesc', { name: pendingExerciseName })}
        confirmText={t('common.add')}
      >
        <div className="mt-3 space-y-2">
          <Label className="text-sm font-medium">{t('workout.muscleGroupLabel')}</Label>
          <Select value={pendingMuscleGroup} onValueChange={setPendingMuscleGroup}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('workout.muscleGroupPh')} />
            </SelectTrigger>
            <SelectContent>
              {muscleGroups.map((g) => (
                <SelectItem key={g.value} value={g.value}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">
            {t('workout.dictTrainerNote')}
          </div>
        </div>
      </ExerciseConfirmDialog>

      {/* Removed Set Deletion Confirmation Dialog - now deletes directly */}

      {/* Client Profile Modal */}
      {selectedClient && (
        <ClientProfileModal
          show={showClientProfile}
          onHide={() => setShowClientProfile(false)}
          client={selectedClient}
          trainerId={trainerId || workout?.trainer_id}
          onWorkoutUpdate={onWorkoutUpdate}
          onExerciseUpdate={onExerciseUpdate}
        />
      )}

      {/* Client Picker (for group workouts) */}
      <AlertDialog open={showClientPicker} onOpenChange={setShowClientPicker}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workout.pickClientHeader')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isSwitchingClientWorkout ? t('workout.openingClientWorkout') : t('workout.willOpenClientWorkout')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {(Array.isArray(workout?.allClients) ? workout.allClients : []).map((c) => (
              <Button
                key={c?.id || `${c?.name}-${c?.nickname}`}
                type="button"
                variant="ghost"
                className="w-full justify-start h-12"
                disabled={isSwitchingClientWorkout || !c?.id}
                onClick={() => handleOpenWorkoutForClient(c.id)}
              >
                <Avatar className="h-8 w-8 mr-3">
                  <AvatarImage src={c?.avatar} alt={c?.name || c?.nickname} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {(c?.name || c?.nickname || '?').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {displayClientName(c?.name) || t('clients.unnamed')}
                  </div>
                </div>
              </Button>
            ))}
          </div>

          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel disabled={isSwitchingClientWorkout}>{t('common.cancel')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Workout Confirmation Dialog */}
      <AlertDialog open={showRestoreWorkoutDialog} onOpenChange={(open) => { if (!isRestoring) setShowRestoreWorkoutDialog(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workout.restoreTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workout.restoreDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={confirmRestoreWorkout}
              disabled={isRestoring}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              {isRestoring ? t('workout.restoring') : t('workout.restore')}
            </Button>
            <AlertDialogCancel disabled={isRestoring} className="w-full">{t('common.cancel')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Workout Confirmation Dialog */}
      <AlertDialog open={showCancelWorkoutDialog} onOpenChange={(open) => { if (!isCancelling) setShowCancelWorkoutDialog(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workout.cancelWorkoutTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={confirmCancelWorkout}
              disabled={isCancelling}
              variant="destructive"
              className="w-full"
            >
              {isCancelling ? t('workout.cancelling') : t('workout.cancelThisWorkout')}
            </Button>
            {canCancelFutureInSeries && (
              <Button
                onClick={confirmCancelWorkoutAndFuture}
                disabled={isCancelling}
                variant="destructive"
                className="w-full"
              >
                {isCancelling ? t('workout.cancelling') : t('workout.cancelFutureWorkouts')}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={cancelCancelWorkout}
              disabled={isCancelling}
              className="w-full"
            >
              {t('workout.back')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Skip Workout Confirmation Dialog */}
      <AlertDialog open={showSkipWorkoutDialog} onOpenChange={setShowSkipWorkoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workout.skipTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workout.skipDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSkipWorkout}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSkipWorkout}
              disabled={isSkipping}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSkipping ? t('workout.markingSkip') : t('workout.markSkipBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Plan Assignment Modal */}
      <PlanAssignmentModal
        show={showPlanAssignment}
        onHide={() => setShowPlanAssignment(false)}
        onPlanSelect={handlePlanSelectFromModal}
        onManualCreate={handleManualExerciseCreate}
        trainerId={workout?.trainer_id || (() => {
          try {
            const savedUser = localStorage.getItem('currentUser');
            if (savedUser) return JSON.parse(savedUser)?.id;
          } catch (e) {
            // ignore
          }
          return 'default-trainer';
        })()}
        currentPlanId={workoutPlanId}
      />

    </>
  );
};

export default WorkoutDetailModal;
